import convert from "convert-units";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import lround from "lodash/round";
import moment from "moment";
import React from "react";
import { createPortal } from "react-dom";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import { PARAMETER_OPTIONS } from "../../constants";
import { RillContext } from "../../rill/RillReact/share/base";
import {
  getButtonStates,
  isSetRowEmpty,
} from "../../utils/add-exercise-buttons";
import { shouldConfirmExerciseSwitch } from "../../utils/add-exercise-switch";
import {
  applyBulkChange,
  buildEditItems,
  buildEditSavePayload,
  exerciseToRawForm,
  getProgramWorkout,
  isEditableItem,
  itemState,
  layoutSignature,
  reconcileDraft,
  rejectedIdsKey,
  summarizeChanges,
} from "../../utils/edit-workout";
import { describeInsert, getInsertSession } from "../../utils/exercise-insert";
import {
  getLibraryMetadataNoticeState,
  libraryMetadataSelectionKey,
  mergeSavedMetadataIntoExercise,
} from "../../utils/exercise-library-metadata";
import {
  prevalidateOtherSet,
  prevalidateSet,
  validateAssistantSet,
  validateExercise,
  validateSet,
} from "../../utils/exercise-validations";
import i18n from "../../i18n";
import { joinRpeForEdit, splitRpeText } from "../../utils/rpe-range";
import {
  excludeWorkoutNotes,
  pickWorkoutNote,
} from "../../utils/section-types";
import { saveLibraryMetadataIfRequested as saveLibraryMetadata } from "../../utils/submit-library-metadata-from-add-exercise";
import {
  buildQueueStorageKey,
  clearQueue,
  countQueueExercises,
  generateQueueItemId,
  isQueueExercise,
  loadQueue,
  persistQueue,
  sanitizeQueueLinks,
  sweepStaleQueues,
} from "../../utils/workout-queue-helpers";
import { getPermissionNamesByIds } from "../../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { SearchDropdown } from "../inputs/search-dropdown";
import UnitInput from "../inputs/unit-input-add-exercise";
import Modal from "../modal-new";
import { mapParameter } from "../other-exercise/CustomParametersSets";
import SvgIcon from "../SvgIcon";
import Toast from "../toast";
import AddComplex from "./add_edit_exercise/AddComplex";
import AddExerciseSets from "./add_edit_exercise/AddExerciseSets";
import AddExerciseTabs from "./add_edit_exercise/AddExerciseTabs";
import AddLibraryExercise from "./add_edit_exercise/AddLibraryExercise";
import AddMetcon from "./add_edit_exercise/AddMetcon";
import AddSetRow from "./add_edit_exercise/AddSetRow";
import ExerciseLibraryMetadataGapsNotice from "./add_edit_exercise/components/ExerciseLibraryMetadataGapsNotice";
import EditSetRow from "./add_edit_exercise/EditSetRow";
import SetRowHint from "./add_edit_exercise/SetRowHint";
import {
  ADD_EXERCISE_PICKER_ID,
  ADD_EXERCISE_PRIMARY_ACTION_ID,
  focusAfterSetCommit,
  focusExercisePicker,
  preventImplicitEnterSubmit,
} from "./add_edit_exercise/set-entry-keyboard";
import WorkoutQueuePanel from "./add_edit_exercise/WorkoutQueuePanel";
import { getExerciseName } from "./learn-more/blockHelpers";

const { SETS, REPS, RPEAMRAP, LOAD, NONE } = PARAMETER_OPTIONS;

class AddExerciseModal extends React.Component {
  // newSet.load and newSet.equippedRepMax are always the same units as the user's preferred display units
  // defaultRepMax is always stored in kg
  constructor(props, context) {
    super(props, context);
    this.locals = context.locals;
    this.activeUser =
      cloneDeep(this.locals.athlete) ||
      cloneDeep(this.locals.activeUserToRequest);
    this.activeUser.preferredLoadInput =
      this.locals.activeUserToRequest.preferredLoadInput;

    this.editExercise = cloneDeep(this.props.editExercise);
    this.sessionId = parseInt(context.locals.query.session, 10)
      ? context.locals.query.session
      : get(context.locals.query.session, "order");
    // Edit Workout (sc-47391): Edit on an exercise opens its whole workout in
    // the queue. Metcons (addType=metcon) keep the single-exercise edit, and
    // so do pages that don't load the workout's sections (the athlete's own
    // dashboard calendars): saving without them would drop every section.
    const { editMode, addType, exercise: editTargetId } = context.locals.query;
    this.editWorkoutSlot =
      editMode && (!addType || addType === "single")
        ? this.findEditWorkoutSlot(editTargetId)
        : null;
    this.editWorkout = Boolean(this.editWorkoutSlot);
    this.editExerciseMode = editMode && !this.editWorkout;

    let defaultRepMax = 0;
    let defaultSecondaryRepMax = 0;
    let secondaryValue = "";

    if (this.editExerciseMode) {
      if (this.editExercise?.type === "primary") {
        secondaryValue = `${this.editExercise.primary}`;
        defaultRepMax = get(
          this.editExercise,
          `repMaximums.${this.editExercise.primary || "squat"}`,
          100
        );
      } else if (this.editExercise?.type === "secondary") {
        secondaryValue = `${this.editExercise.secondary}{${this.editExercise.primary}}`;

        // Getting defaultRepMax because it is used for adding new secondaries
        defaultRepMax = get(
          this.editExercise,
          `repMaximums.${this.editExercise.primary || "squat"}`,
          100
        );
        if (this.editExercise.secondaryRepMaximum) {
          defaultSecondaryRepMax = this.editExercise.secondaryRepMaximum;
        } else {
          const movement = this.activeUser.exerciseLibrary[
            this.editExercise.primary
          ].find((item) => item.movement === this.editExercise.secondary);
          defaultSecondaryRepMax = movement.repMaximum;
        }
      } else {
        secondaryValue = `${this.editExercise?.secondary}{${this.editExercise?.primary}}`;
      }
    } else {
      defaultRepMax = get(this.activeUser, `repMaximums.squat`, 0);
    }

    this.state = {
      selectInput: "",
      addingFromSelect: false,
      form: {
        primary: this.editExerciseMode ? this.editExercise.primary : "squat",
        secondary: this.editExerciseMode ? secondaryValue.split("{")[0] : "",
        secondaryValue: this.editExerciseMode ? secondaryValue : "",
        exerciseMovement: this.editExerciseMode
          ? this.editExercise.secondary
          : "",
        sets: this.editExerciseMode ? this.editExercise.sets : [],
        type: this.editExerciseMode ? this.editExercise.type : "secondary",
        repMaximums: this.editExerciseMode
          ? this.editExercise.repMaximums
          : get(this.activeUser, `repMaximums`, {
              squat: 0,
              bench: 0,
              deadlift: 0,
            }),
        secondaryRepMaximum: defaultSecondaryRepMax,
        cycle: this.editExerciseMode
          ? this.editExercise.cycle
          : this.editWorkout
            ? this.editWorkoutSlot.target.cycle
            : {
                id: get(
                  context.locals.params,
                  "cycleId",
                  context.locals.query.cycle
                ),
                isScheduled: false,
                program: context.locals.query.program,
                programWeek:
                  context.locals.query.programWeek || context.locals.query.week,
                programSession:
                  context.locals.query.programSession || this.sessionId,
              },
        notes: this.editExerciseMode ? this.editExercise.notes : "",
        repsPerSide: this.editExerciseMode
          ? !!this.editExercise.repsPerSide
          : false,
        tempo: this.editExerciseMode ? this.editExercise.tempo || "" : "",
        basicExerciseType: "basicExercise",
      },
      tempoChecked: this.editExerciseMode ? !!this.editExercise.tempo : false,
      tempoError: false,
      defaultRepMax: defaultRepMax,
      newSet: {
        units: {
          defaultRepMax: this.locals.activeUser.units,
          load: this.locals.activeUser.units,
          equippedRepMax: this.locals.activeUser.units,
        },
      },
      isNewSetValid: false,
      // Set group / queue card that was just committed; flashes for a moment.
      highlightSet: null,
      highlightQueueId: null,
      editSet: {
        units: {
          defaultRepMax: this.locals.activeUser.units,
          load: this.locals.activeUser.units,
          equippedRepMax: this.locals.activeUser.units,
        },
      },
      editExerciseMode: this.editExerciseMode,
      preferredLoadInput: get(this.activeUser, "preferredLoadInput", "percent"),
      updateRMPrompt: false,
      newSecondaryUnit: this.locals.activeUser.units,
      // states for adding exercise's fields
      newExerciseFields: {
        parameters: [SETS, REPS, RPEAMRAP, LOAD],
        freeFormSelected: null,
        videoUrl: "",
        videoFile: null,
        videoOriginal: "", // if there is an uploaded video
        exerciseTypeField: "",
        primaryMuscleGroup: "",
        secondaryMuscleGroup: "",
        instructions: "",
      },
      parametersForm: {
        sets: "",
        rounds: "",
        reps: "",
        rpe: "",
        time: "",
        distance: "",
        load: "",
        rest: "",
        velocity: "",
        freeform: "",
      },
      newMovement: "",
      submited: false,
      error: false,
      queueItems: [],
      editingQueueIndex: null,
      queueWorkoutName: "",
      queueSaving: false,
      // The queue's single note draft. `queueNoteDirty` is the whole safety
      // story for the upsert on save: untouched (false) means the field is
      // omitted from the save request entirely (server leaves any existing
      // note alone); touched (true) sends the current text verbatim,
      // including "" to delete a prefilled note.
      queueNote: "",
      queueNoteExpanded: false,
      queueNoteDirty: false,
      // Edit Workout: where a new exercise lands (null = end of the list),
      // the inline "discard changes?" footer bar, and the bulk-apply toast.
      editNewIndex: null,
      discardBarOpen: false,
      bulkToast: null,
    };

    // The destination (program session or calendar day) is fixed for the
    // life of this mount, so the storage key can be computed once.
    this.queueStorageKey = buildQueueStorageKey(
      this.props.date
        ? { date: moment.utc(this.props.date).format("YYYYMMDD") }
        : { cycle: this.state.form.cycle },
      context.locals.query.athlete
    );
    // Edit Workout drafts survive the in-modal flows that refresh the page
    // (RM update, Create new exercise) and a failed save.
    this.editStorageKey = `${this.queueStorageKey}.edit`;
    // Insert mode: the Insert group of the exercise ••• menu opens this modal
    // with insertTarget + insertPosition. Without insertTarget (or in the
    // calendar / edit mode) none of this applies and the modal is unchanged.
    const { insertTarget, insertPosition } = context.locals.query;
    // Program editor: the cycle-wide editExercises/editSections; calendar: the
    // athlete's day out of activeExercises/activeSections.
    this.insertSession =
      insertTarget && !this.editExerciseMode
        ? this.props.date
          ? getInsertSession(
              this.locals.activeExercises,
              this.locals.activeSections,
              insertTarget,
              { day: this.props.date.format("YYYYMMDD") }
            )
          : getInsertSession(
              this.locals.editExercises,
              this.locals.editSections,
              insertTarget
            )
        : null;
    if (this.insertSession) {
      this.state.insertPosition = ["above", "below"].includes(insertPosition)
        ? insertPosition
        : "below";
      // a queue built for one target must not restore into another spot
      this.queueStorageKey = `${this.queueStorageKey}.ins.${insertTarget}`;
    }
    this.handleInsertPositionChange = (insertPosition) =>
      this.setState({ insertPosition });
    this.handleEditSetChange = this.handleEditSetChange.bind(this);
    this.handleRemoveSet = this.handleRemoveSet.bind(this);
    this.handleUpdateSet = this.handleUpdateSet.bind(this);
    this.handleSetEditModeToggle = this.handleSetEditModeToggle.bind(this);
    this.handleUnitChange = this.handleUnitChange.bind(this);
    this.confirmLeavePage = this.confirmLeavePage.bind(this);
    this.updateRM = this.updateRM.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleRMChange = this.handleRMChange.bind(this);
    this.handleSaveToLibrary = this.handleSaveToLibrary.bind(this);
    this.buildMovementOptionsArray = this.buildMovementOptionsArray.bind(this);
    this.updateOtherExerciseParameters =
      this.updateOtherExerciseParameters.bind(this);
    this.handleNewFieldsInputChange =
      this.handleNewFieldsInputChange.bind(this);
    this.finishSaveToLibrary = this.finishSaveToLibrary.bind(this);
    this.handleParameterInputChange =
      this.handleParameterInputChange.bind(this);
    this.handleReorderSet = this.handleReorderSet.bind(this);
    this.handleNewSetChange = this.handleNewSetChange.bind(this);
    this.handleAddSet = this.handleAddSet.bind(this);
    this.handleExerciseOption = this.handleExerciseOption.bind(this);
    this.toggleRepsPerSide = this.toggleRepsPerSide.bind(this);
    this.toggleTempo = this.toggleTempo.bind(this);
    this.onTempoInput = this.onTempoInput.bind(this);
    this.tempoInputRef = React.createRef();
    this.libraryMetadataPanelRef = React.createRef();
    this.libraryMetadataNotice = { show: false };
    this._submitInProgress = false;
    this.saveLibraryMetadataIfRequested =
      this.saveLibraryMetadataIfRequested.bind(this);
    this.handleAddNextExercise = this.handleAddNextExercise.bind(this);
    this.handleEditQueueItem = this.handleEditQueueItem.bind(this);
    this.handleRemoveQueueItem = this.handleRemoveQueueItem.bind(this);
    this.handleToggleQueueLink = this.handleToggleQueueLink.bind(this);
    this.handleQueueItemsChange = this.handleQueueItemsChange.bind(this);
    this.handleAddQueueSection = this.handleAddQueueSection.bind(this);
    this.handleRenameQueueSection = this.handleRenameQueueSection.bind(this);
    this.handleQueueWorkoutNameChange =
      this.handleQueueWorkoutNameChange.bind(this);
    this.handleQueueNoteChange = this.handleQueueNoteChange.bind(this);
    this.handleToggleQueueNote = this.handleToggleQueueNote.bind(this);
    this.handleSaveQueue = this.handleSaveQueue.bind(this);
    this.handleQueueCancel = this.handleQueueCancel.bind(this);
  }

  /** Org/user configured default load-input mode (e.g. "percent"). */
  defaultPreferredLoadInput() {
    return get(this.activeUser, "preferredLoadInput", "percent");
  }

  /** Assistant exercises have no %1RM, so they can't use "percent". */
  preferredLoadInputForType(type) {
    return type === "assistant" ? "weight" : this.defaultPreferredLoadInput();
  }

  /** Reflect how a specific already-entered set was recorded, not the current default. */
  preferredLoadInputForSet(set) {
    if (set.loadDrop) return "drop";
    if (this.state.form.type !== "assistant" && set.percentRepMax)
      return "percent";
    return "weight";
  }

  /**
   * The workout queue only applies when ADDING single exercises; editing an
   * already-saved exercise and the Complexes/Metcon tabs keep the classic
   * one-shot save flow. A getter (not a constructor-time value) because
   * switching tabs re-renders this same mounted instance with a new
   * `query.addType` — it never remounts.
   */
  get queueEnabled() {
    const { addType } = this.context.locals.query;
    return (
      this.editWorkout ||
      (!this.editExerciseMode && (!addType || addType === "single"))
    );
  }

  componentDidMount() {
    const {
      query: { editMode, exercise },
      editExercises,
      activeExercises,
    } = this.context.locals;

    const arr = editExercises || activeExercises;
    if (editMode && exercise && arr) {
      const selectedExercise = arr.find((elem) => elem._id === exercise);
      this.setState({ selectedExercise });
    }

    // Evict stale queues for *any* destination, not just this one — a coach
    // with many athletes/cycles/calendar days can otherwise accumulate
    // abandoned keys past their TTL indefinitely (lazy per-key expiry only
    // fires for a destination the coach happens to revisit).
    sweepStaleQueues();

    if (this.editWorkout) {
      this.loadEditWorkout();
      this.warnBeforeUnload = (event) => {
        if (!this.state.queueSaving && this.editChanges().any) {
          event.preventDefault();
          event.returnValue = "";
        }
      };
      window.addEventListener("beforeunload", this.warnBeforeUnload);
      // Esc asks before closing, like the close button. Keys a field already
      // handled (set row edit, section rename, open select) are left alone.
      this.closeOnEscape = (event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        if (this.state.discardBarOpen) {
          this.setState({ discardBarOpen: false });
          return;
        }
        this.handleEditClose();
      };
      window.addEventListener("keydown", this.closeOnEscape);
      return;
    }

    // Restore a persisted queue for this destination. The queue lives in
    // localStorage so it survives the in-modal flows that trigger a full
    // page refresh (Create new exercise, RM update, tab switches).
    if (this.queueEnabled) {
      const stored = loadQueue(this.queueStorageKey);
      const queueItems = (stored?.items || []).map((item) =>
        item.id ? item : { ...item, id: generateQueueItemId() }
      );
      const queueWorkoutName =
        stored?.workoutName ||
        (this.props.date ? "" : this.getSessionWorkoutName());
      // A persisted localStorage draft (the coach already touched the note
      // row this session, even to clear it) wins over the server value —
      // same precedence the queue already applies to workoutName above.
      // Otherwise prefill from whatever note already exists on the target
      // day/session (already loaded via activeSections/editSections, no
      // extra request) so "+ Add" effectively becomes "edit".
      const queueNoteDirty = typeof stored?.note === "string";
      const queueNote = queueNoteDirty
        ? stored.note
        : this.getExistingWorkoutNote()?.description || "";
      if (queueItems.length || queueWorkoutName || queueNote) {
        this.setState({
          queueItems,
          queueWorkoutName,
          queueNote,
          queueNoteDirty,
          // Visible rather than hidden behind "+ Add" when there's already
          // something to show (restored draft or server prefill).
          queueNoteExpanded: Boolean(queueNote),
        });
      }
    }

    // Editing an exercise that already has sets: land in the sets field.
    // A new exercise focuses the movement search instead (see SearchDropdown).
    if (this.editExerciseMode) this.focusSetEntry();
  }

  /**
   * The saved workout (day or program session) holding `targetId`, from the
   * lists this page already loaded. Null when the page has no sections
   * loaded: saving without them would drop every section.
   */
  findEditWorkoutSlot(targetId) {
    const locals = this.context?.locals || this.locals;
    if (this.props.date) {
      if (!Array.isArray(locals.activeSections)) return null;
      return getInsertSession(
        locals.activeExercises,
        locals.activeSections,
        targetId,
        { day: this.props.date.format("YYYYMMDD") }
      );
    }
    if (!Array.isArray(locals.editSections)) return null;
    return getProgramWorkout(
      locals.editCycle,
      locals.editExercises,
      locals.editSections,
      targetId
    );
  }

  /**
   * Edit Workout: the saved workout as queue items. A draft left by a page
   * refresh (or a failed save) is restored when it was built from the same
   * saved exercises; otherwise the workout changed and the draft is dropped.
   */
  loadEditWorkout() {
    const { exercises, sections } = this.editWorkoutSlot;
    const saved = buildEditItems(exercises, excludeWorkoutNotes(sections));
    this.editInitialLayout = layoutSignature(saved);
    this.editExpectedIds = buildEditSavePayload(saved).expectedIds;
    this.editExpectedSectionIds = saved
      .filter((item) => item.kind === "section")
      .map((item) => item.serverId);

    const stored = loadQueue(this.editStorageKey);
    const sameWorkout =
      Array.isArray(stored?.expectedIds) &&
      [...stored.expectedIds].sort().join() ===
        [...this.editExpectedIds].sort().join();
    if (stored && !sameWorkout) clearQueue(this.editStorageKey);
    const queueItems = sameWorkout
      ? reconcileDraft(stored.items, saved, this.takeRejectedIds())
      : saved;
    const queueNoteDirty = sameWorkout && typeof stored.note === "string";
    const queueNote = queueNoteDirty
      ? stored.note
      : this.getExistingWorkoutNote()?.description || "";

    const targetId = String(this.context.locals.query.exercise);
    const targetIndex = queueItems.findIndex(
      (item) =>
        item.serverId === targetId || item.complexIds?.includes(targetId)
    );
    const firstEditable = queueItems.findIndex(isEditableItem);
    const selected = isEditableItem(queueItems[targetIndex])
      ? targetIndex
      : firstEditable;

    this.setState(
      {
        queueItems,
        queueNote,
        queueNoteDirty,
        queueNoteExpanded: Boolean(queueNote),
      },
      () => {
        if (selected !== -1) this.handleEditQueueItem(selected);
        else this.startNewEditExercise();
      }
    );
  }

  /** The ids the API rejected on the last save (422), read once. */
  takeRejectedIds() {
    try {
      const key = rejectedIdsKey(this.editStorageKey);
      const ids = JSON.parse(localStorage.getItem(key) || "[]");
      localStorage.removeItem(key);
      return Array.isArray(ids) ? ids : [];
    } catch {
      return [];
    }
  }

  /** Changes not saved yet (exercises, layout, note). */
  editChanges() {
    const summary = summarizeChanges(
      this.state.queueItems,
      this.editInitialLayout
    );
    const count = summary.edited + summary.added + summary.removed;
    return {
      ...summary,
      count,
      any:
        count > 0 ||
        summary.layoutChanged ||
        this.state.queueNoteDirty ||
        this.editFormChanged(),
    };
  }

  /** The editor form differs from the card it was loaded from. */
  editFormChanged() {
    if (this.state.isNewSetValid) return true;
    const item = this.state.queueItems[this.state.editingQueueIndex];
    if (!item) {
      // Building a new exercise: anything picked or entered counts.
      return Boolean(
        this.state.form.secondaryValue || this.state.form.sets?.length
      );
    }
    if (item.locked || item.removed) return false;
    return this.editFormSnapshot() !== this.loadedEditForm;
  }

  editFormSnapshot() {
    return JSON.stringify({
      ...this.state.form,
      tempoChecked: this.state.tempoChecked,
    });
  }

  persistEditState(items, note) {
    persistQueue(
      this.editStorageKey,
      items ?? this.state.queueItems,
      "",
      note ?? (this.state.queueNoteDirty ? this.state.queueNote : undefined),
      this.editExpectedIds
    );
  }

  /** Same items, new list: drafts are persisted and the open card kept by id. */
  setEditItems(items, extraState = {}) {
    const openId = this.state.queueItems[this.state.editingQueueIndex]?.id;
    const editingQueueIndex = openId
      ? items.findIndex((item) => item.id === openId)
      : null;
    this.persistEditState(items);
    this.setState({
      queueItems: items,
      editingQueueIndex: editingQueueIndex === -1 ? null : editingQueueIndex,
      ...extraState,
    });
  }

  /**
   * Store the open exercise's form into its card (or insert it as a new
   * card). Resolves false when the form is invalid; the coach stays on it.
   */
  async commitEditForm() {
    if (!this.editFormChanged()) return true;
    await new Promise((resolve) => {
      if (!this.state.isNewSetValid) return resolve();
      this.handleAddSet({
        preventDefault() {},
        focusAfterCommit: "none",
        onCommitted: resolve,
      });
    });
    const built = await this.buildQueuedExercise();
    if (!built) return false;

    const { queueItems, editingQueueIndex, editNewIndex } = this.state;
    let items;
    let committedIndex;
    if (queueItems[editingQueueIndex]) {
      committedIndex = editingQueueIndex;
      items = queueItems.map((item, index) =>
        index === editingQueueIndex ? { ...item, ...built } : item
      );
    } else {
      committedIndex = editNewIndex ?? queueItems.length;
      items = [...queueItems];
      items.splice(committedIndex, 0, {
        kind: "exercise",
        id: generateQueueItemId(),
        linkedToPrev: false,
        ...built,
      });
    }
    this.persistEditState(items);
    await new Promise((resolve) =>
      this.setState(
        {
          queueItems: items,
          editingQueueIndex: committedIndex,
          editNewIndex: null,
        },
        resolve
      )
    );
    this.loadedEditForm = this.editFormSnapshot();
    this.flashHighlight("highlightQueueId", items[committedIndex].id);
    return true;
  }

  /** Card click, pencil, Previous / Next. */
  async selectEditItem(index) {
    if (index === this.state.editingQueueIndex) return;
    const item = this.state.queueItems[index];
    if (item?.metcon) {
      this.openMetconEditor(item);
      return;
    }
    if (!isEditableItem(item)) return;
    if (!(await this.commitEditForm())) return;
    this.handleEditQueueItem(index);
  }

  /** Previous / Next skip metcons and complexes. */
  stepEditItem(step) {
    const { queueItems, editingQueueIndex } = this.state;
    for (
      let index = (editingQueueIndex ?? queueItems.length) + step;
      index >= 0 && index < queueItems.length;
      index += step
    ) {
      if (isEditableItem(queueItems[index])) {
        this.selectEditItem(index).then(() => focusExercisePicker());
        return;
      }
    }
  }

  /** Metcons keep their own editor; unsaved changes are confirmed first. */
  openMetconEditor(item) {
    if (this.editChanges().any) {
      this.setState({ discardBarOpen: { metcon: item.serverId } });
      return;
    }
    this.goToMetconEditor(item.serverId);
  }

  goToMetconEditor(exerciseId) {
    clearQueue(this.editStorageKey);
    submit("/actions/refresh?", {
      method: "GET",
      body: { addType: "metcon", exercise: exerciseId, editMode: true },
    });
  }

  /** Footer "Add exercise": a new card at the end of the open card's section. */
  async startNewEditExercise() {
    if (!(await this.commitEditForm())) return;
    const { queueItems, editingQueueIndex } = this.state;
    let editNewIndex = null;
    if (editingQueueIndex !== null) {
      editNewIndex = editingQueueIndex + 1;
      while (
        editNewIndex < queueItems.length &&
        queueItems[editNewIndex].kind !== "section"
      )
        editNewIndex++;
    }
    this.loadedEditForm = null;
    this.setState({ editingQueueIndex: null, editNewIndex });
    this.resetFormForNextExercise(() => focusExercisePicker());
  }

  /** Undo changes: the open card back to how it was loaded. */
  undoEditItem() {
    const index = this.state.editingQueueIndex;
    const items = this.state.queueItems.map((item, i) =>
      i === index ? { ...item, exercise: null, rawForm: null } : item
    );
    this.persistEditState(items);
    this.setState({ queueItems: items }, () => this.handleEditQueueItem(index));
  }

  /** Change many at once: open form committed first, then every card in scope. */
  async handleBulkApply(fields, scope) {
    if (!(await this.commitEditForm())) return null;
    const toKg = (value) =>
      this.locals.activeUser.units === "imperial"
        ? convert(value).from("lb").to("kg")
        : value;
    const { items, affected } = applyBulkChange(
      this.state.queueItems,
      fields,
      scope,
      toKg
    );
    this.persistEditState(items);
    const openIndex = this.state.editingQueueIndex;
    this.setState({ queueItems: items }, () => {
      if (openIndex !== null && items[openIndex]?.rawForm === null)
        this.handleEditQueueItem(openIndex);
    });
    this.showBulkToast(
      this.props.t("editWorkout.appliedToast", { count: affected })
    );
    return affected;
  }

  showBulkToast(message) {
    clearTimeout(this.bulkToastTimer);
    this.setState({ bulkToast: message });
    this.bulkToastTimer = setTimeout(
      () => this.setState({ bulkToast: null }),
      5000
    );
  }

  /**
   * Cancel (no event), or the close / backdrop links (which close by
   * navigating unless prevented): ask inline when something would be lost.
   */
  handleEditClose(e) {
    if (this.editChanges().any) {
      e?.preventDefault();
      this.setState({ discardBarOpen: true });
      return;
    }
    clearQueue(this.editStorageKey);
    if (!e) this.closeModal();
  }

  discardEditChanges() {
    const pending = this.state.discardBarOpen;
    clearQueue(this.editStorageKey);
    if (pending?.metcon) {
      this.goToMetconEditor(pending.metcon);
      return;
    }
    this.closeModal();
  }

  closeModal() {
    submit("/actions/refresh/", {
      method: "GET",
      body: {
        modal: "",
        editMode: "",
        exercise: "",
        insertTarget: "",
        insertPosition: "",
      },
    });
  }

  /** The single save: every change of the workout in one request. */
  async handleSaveWorkoutEdit() {
    if (this.state.queueSaving) return;
    if (!(await this.commitEditForm())) return;
    if (!this.editChanges().any) return;

    const { items, removed } = buildEditSavePayload(this.state.queueItems);
    const { count } = this.editChanges();
    this.savingLocals = this.context.locals;
    this.setState({ queueSaving: true });
    submit(
      `/actions/exercise/save-workout-edit?proxyAthlete=${this.locals.query.athlete}&reqSource=${this.props.reqSource}`,
      {
        method: "POST",
        body: {
          target: this.props.date
            ? { date: this.props.date.clone().toDate() }
            : { cycle: this.editWorkoutSlot.target.cycle },
          // What this modal loaded; the API refuses the save (409) when the
          // saved workout no longer matches.
          expectedIds: this.editExpectedIds,
          expectedSectionIds: this.editExpectedSectionIds,
          items,
          removed,
          workoutNote: this.state.queueNoteDirty
            ? this.state.queueNote
            : undefined,
          changeCount: count,
          editStorageKey: this.editStorageKey,
          organizationCoach: this.locals.activeUserToRequest._id,
        },
      }
    );
  }

  /**
   * The target day's (calendar) or session's (program) existing workout
   * note, if any — read from the sections already loaded by this page's
   * loaders (activeSections/editSections), no extra request. Used only to
   * prefill the queue's note row; the actual upsert decision is made
   * server-side at save time against the then-current note.
   */
  getExistingWorkoutNote() {
    const { activeSections, editSections } = this.context.locals;
    if (this.props.date) {
      const utcDate = Number(moment.utc(this.props.date).format("YYYYMMDD"));
      return pickWorkoutNote(
        (activeSections || []).filter((s) => s.utcDate === utcDate)
      );
    }
    const cycle = this.state.form.cycle;
    if (!cycle) return null;
    return pickWorkoutNote(
      (editSections || []).filter(
        (s) =>
          s.cycle &&
          String(s.cycle.id) === String(cycle.id) &&
          s.cycle.program === Number(cycle.program) &&
          s.cycle.programWeek === Number(cycle.programWeek) &&
          s.cycle.programSession === Number(cycle.programSession)
      )
    );
  }

  /** Current workoutName of the target program session (prefill). */
  getSessionWorkoutName() {
    const { editCycle } = this.context.locals;
    const cycle = this.state.form.cycle;
    if (!editCycle || !cycle) return "";
    const program = (editCycle.programs || []).find(
      (p) => p.order === Number(cycle.program)
    );
    const session = (program?.sessions || []).find(
      (s) =>
        s.week === Number(cycle.programWeek) &&
        s.order === Number(cycle.programSession)
    );
    return session?.workoutName || "";
  }

  componentDidUpdate(prevProps) {
    // A failed save redirects back to this page without unmounting the
    // modal: the new request's locals mean the attempt is over. The modal
    // reloads the saved workout: a 409 dropped the drafts, a 422 kept them
    // and they are reconciled with what the athlete logged.
    if (
      this.editWorkout &&
      this.state.queueSaving &&
      this.context.locals !== this.savingLocals
    ) {
      this.locals = this.context.locals;
      this.setState({ queueSaving: false });
      const slot = this.findEditWorkoutSlot(this.context.locals.query.exercise);
      if (slot) {
        this.editWorkoutSlot = slot;
        this.loadedEditForm = null;
        this.loadEditWorkout();
      }
    }
    // if a new exercise is added to the assistant library
    const { lengthAssistantLibrary, secondaryAdded } = this.props;
    if (lengthAssistantLibrary !== prevProps.lengthAssistantLibrary) {
      const target = {
        name: "secondaryValue",
        value: this.state.form.secondary,
        primary: "assistant",
        type: "assistant",
        newMovementRM: 0,
      };

      this.handleChange({ target });
    } else if (secondaryAdded && secondaryAdded !== prevProps.secondaryAdded) {
      const target = {
        name: "secondaryValue",
        value: this.state.form.secondary,
        primary: this.state.form.primary,
        type: "secondary",
        newMovementRM: this.state.form.secondaryRepMaximum,
      };
      this.handleChange({ target });
    }
  }

  updateOtherExerciseParameters(parameters, freeFormSelected) {
    this.setState((prevState) => ({
      newExerciseFields: {
        ...prevState.newExerciseFields,
        parameters,
        freeFormSelected,
      },
    }));
  }

  handleChange(e) {
    const { name, value, movementPrimary, type, newMovementRM } = e.target;
    const { newSet, form: constForm } = this.state;
    let { form, defaultRepMax } = this.state;

    const primary =
      name === "exerciseMovement" && constForm.primary
        ? movementPrimary
        : constForm.primary;

    form[name] = value.split("{")[0];
    form.primary = primary;

    if (name === "basicExerciseType" && value === "powerliftingExercise") {
      defaultRepMax = 100;
    }

    if (name === "exerciseMovement") {
      if (type === "primary") {
        form.type = "primary";

        // updates the repMaximum based on the new primary e.g changing from 'squat' to 'bench'
        defaultRepMax = form.repMaximums[primary] || 100;
        // updates repMaximum that displays on the form
        if (document.getElementById("defaultRepmaxUnitInput")) {
          document.getElementById("defaultRepmaxUnitInput").value = this.locals
            .activeUserToRequest.round
            ? lround(
                convert(defaultRepMax)
                  .from("kg")
                  .to(newSet.units.defaultRepMax === "imperial" ? "lb" : "kg"),
                0
              )
            : lround(
                convert(defaultRepMax)
                  .from("kg")
                  .to(newSet.units.defaultRepMax === "imperial" ? "lb" : "kg"),
                2
              );
        }
      } else if (type === "secondary") {
        form.type = "secondary";
        form.secondary = value.split("{")[0];
        form.secondaryValue = value;

        /**if the user doesn't have that value in the exercise library, we are going to create the property with
         * an empty array to push the exercise there, this is for weird cases where the users doesnt have that exercise.primary
         * value in the exercise library. EXAMPLE:
         * user.exerciseLibrary = {squat: ..., assistant: ..., deadlift: ...] here doesn't exists "bench"
         * So if an user tries to add a bench variaty in the exercise library this is going to fail because we are pushing an
         * exercise in a undefined value instead in an array.
         **/
        if (
          !this.activeUser.exerciseLibrary[primary] &&
          [
            "assistant",
            "squat",
            "bench",
            "deadlift",
            "clean",
            "jerk",
            "snatch",
            "overheadPress",
            "other",
          ].includes(primary)
        ) {
          this.activeUser.exerciseLibrary[primary] = [];
        }
        const movement = this.activeUser.exerciseLibrary[primary].find(
          (item) => item.movement === form.exerciseMovement
        );

        if (movement) {
          form = Object.assign(form, {
            secondaryRepMaximum:
              movement.repMaximum || this.state.defaultRepMax,
          });
        } else if (this.locals.athlete) {
          form = Object.assign(form, {
            secondaryRepMaximum: form.repMaximums[form.primary] || 100,
          });
        }

        if (document.getElementById("defaultRepmaxUnitInput")) {
          document.getElementById("defaultRepmaxUnitInput").value = this.locals
            .activeUserToRequest.round
            ? lround(
                convert(form.secondaryRepMaximum)
                  .from("kg")
                  .to(newSet.units.defaultRepMax === "imperial" ? "lb" : "kg"),
                0
              )
            : lround(
                convert(form.secondaryRepMaximum)
                  .from("kg")
                  .to(newSet.units.defaultRepMax === "imperial" ? "lb" : "kg"),
                2
              );
        }
      } else {
        form.type = "assistant";
      }
    }

    if (name === "secondaryValue") {
      form.secondaryValue = `${value}{${primary}}`;
      form.type = type;
      form.secondaryRepMaximum = newMovementRM;
      form.selectedLabel = `${value}${
        type === "secondary" ? ` (${primary})` : ""
      }`;
    }

    if (name === "primary") {
      form.primary = value;
      defaultRepMax = form.repMaximums[value] || 100;

      if (document.getElementById("newRMUnitInput")) {
        document.getElementById("newRMUnitInput").value = this.locals
          .activeUserToRequest.round
          ? lround(
              convert(defaultRepMax)
                .from("kg")
                .to(newSet.units.defaultRepMax === "imperial" ? "lb" : "kg"),
              0
            )
          : lround(
              convert(defaultRepMax)
                .from("kg")
                .to(newSet.units.defaultRepMax === "imperial" ? "lb" : "kg"),
              2
            );
      }
    }

    if (name === "notes") {
      return this.setState({
        form,
      });
    }
    const search = form.exerciseMovement || value;
    if (!this.context.locals.activeUserToRequest.exerciseLibrary[primary]) {
      this.context.locals.activeUserToRequest.exerciseLibrary[primary] = [];
    }
    const selectedExercise =
      this.context.locals.activeUserToRequest.exerciseLibrary[primary].find(
        (elem) => elem.movement === search
      ); // load the selected exercise in the state
    if (
      selectedExercise?.parameters ||
      this.state.selectedExercise?.parameters
    ) {
      form.sets = [];
    }

    if (name === "basicExerciseType" && value === "powerliftingExercise") {
      form.repMaximums = {
        bench: 100,
        deadlift: 100,
        squat: 100,
        clean: 100,
        jerk: 100,
        snatch: 100,
        overheadPress: 100,
        other: 100,
      };
    }

    this.setState({
      form,
      defaultRepMax,
      isNewSetValid: prevalidateSet(
        this.state.newSet,
        form.type === "assistant"
      ),
      selectedExercise,
    });
  }

  handleRMDismiss(e) {
    e.preventDefault();
    // When deciding not to change the Rep maximum, revert back to the default Rep Max
    const defaultInKg = get(
      this.state.form,
      `repMaximums.${this.state.form.primary}`,
      100
    );
    const defaultRepMax = lround(
      convert(defaultInKg)
        .from("kg")
        .to(this.state.newSet.units.defaultRepMax === "imperial" ? "lb" : "kg"),
      2
    );
    document.getElementById("defaultRepmaxUnitInput").value = defaultRepMax;
    this.setState({
      updateEnabled: false,
      updateRMPrompt: false,
      defaultRepMax: defaultInKg,
    });
  }

  handleRMChange(e, _where) {
    const form = this.state.form;
    const newSet = this.state.newSet;
    const units = newSet.units.defaultRepMax === "metric" ? "kg" : "lb";
    form.secondaryRepMaximum = lround(
      convert(e.target.value).from(units).to("kg"),
      2
    );

    this.setState({ updateEnabled: true, form });
    return false;
  }

  handleRMUpdate(e) {
    e.preventDefault();
    if (!confirm(this.props.t("addExerciseModal.updateRmConfirm"))) {
      return;
    }
    // updates repMaximum for this exercise only
    let defaultRepMax = parseFloat(
      document.getElementById("defaultRepmaxUnitInput").value
    );
    // ensure defaultRepMax is stored in KG
    // TODO: defaultRepMax units should be stored on this.state.form instead of newSet/editSet
    if (this.state.newSet.units.defaultRepMax === "imperial")
      defaultRepMax = convert(defaultRepMax).from("lb").to("kg");

    const form = this.state.form;
    form.repMaximums[form.primary] = defaultRepMax;
    this.setState({
      defaultRepMax,
      form,
      updateEnabled: false,
      updateRMPrompt: true,
    });

    this.updateRM(form);
  }

  updateRM(form) {
    const { athlete, activeUser } = this.locals;
    const user = athlete || activeUser;

    submit(`/actions/rep-maximum/update?proxyAthlete=${user._id}`, {
      method: "POST",
      body: form,
    });
  }

  // function will update all future workout RMs
  handleUpdateExerciseRM(form, e) {
    e.preventDefault();
    if (!confirm(this.props.t("addExerciseModal.updateAllWorkoutsConfirm"))) {
      return;
    }
    const { athlete, activeUser } = this.locals;
    const user = athlete || activeUser;
    form.updateFutureRM = true;

    submit(`/actions/rep-maximum/update?proxyAthlete=${user._id}`, {
      method: "POST",
      body: form,
    });

    this.setState({ updateRMPrompt: false });
  }
  // in case user does not want to update all workouts, simply dismiss message
  handleUpdateExerciseRMDismiss(e) {
    e.preventDefault();
    this.setState({ updateRMPrompt: false });
  }

  componentWillUnmount() {
    Object.values(this.highlightTimers || {}).forEach(clearTimeout);
    clearTimeout(this.bulkToastTimer);
    if (this.warnBeforeUnload)
      window.removeEventListener("beforeunload", this.warnBeforeUnload);
    if (this.closeOnEscape)
      window.removeEventListener("keydown", this.closeOnEscape);
  }

  /** Flash a just-committed set group or queue card; clears itself. */
  flashHighlight(stateKey, value) {
    this.highlightTimers = this.highlightTimers || {};
    clearTimeout(this.highlightTimers[stateKey]);
    this.setState({ [stateKey]: value });
    this.highlightTimers[stateKey] = setTimeout(
      () => this.setState({ [stateKey]: null }),
      2000
    );
  }

  handleAddSetAssistantWParameters(focusAfter = "sets", onCommitted) {
    const { form, selectedExercise, parametersForm } = this.state;
    const { locals } = this.context;
    let newSet = {};
    selectedExercise.parameters.forEach((element) => {
      newSet[mapParameter(element)] = parametersForm[mapParameter(element)];
    });
    if (parametersForm.restTime) newSet.restTime = parametersForm.restTime;
    if (parametersForm.loadDrop) {
      newSet.loadDrop = parametersForm.loadDrop;
      delete newSet.load;
    }

    const rpeError = splitRpeText(newSet);
    if (rpeError) {
      locals.flash("error", i18n.t(`common:${rpeError}`));
      return;
    }

    if (selectedExercise.parameters.includes(REPS)) {
      newSet = validateAssistantSet({ locals, set: newSet });
    }

    if (!newSet) return;

    //form set push
    const cloneForm = cloneDeep(form);
    cloneForm.sets.push(newSet);
    const parameterLoadInput = document.getElementById(`${LOAD}_id_parameter`);

    if (parameterLoadInput) {
      parameterLoadInput.value = 0;
    }

    this.setState(
      {
        form: cloneForm,
        isNewSetValid: false,
        parametersForm: {
          sets: "",
          rounds: "",
          reps: "",
          rpe: "",
          time: "",
          distance: "",
          load: "",
          rest: "",
          velocity: "",
          freeform: "",
          restTime: "",
        },
      },
      () => {
        focusAfterSetCommit(focusAfter);
        onCommitted?.();
      }
    );
    this.flashHighlight("highlightSet", {
      index: cloneForm.sets.length - 1,
      token: Date.now(),
    });
  }

  handleAddSet(e) {
    // Validate newSet and add it to the list of form sets
    e.preventDefault();
    const focusAfter = ["primary", "none"].includes(e.focusAfterCommit)
      ? e.focusAfterCommit
      : "sets";
    const onCommitted = e.onCommitted;
    const { selectedExercise } = this.state;
    const { locals } = this.context;
    this.setState({ error: false });
    if (selectedExercise?.parameters) {
      return this.handleAddSetAssistantWParameters(focusAfter, onCommitted);
    }

    const form = this.state.form;
    const newSet = validateSet({
      exercise: form,
      set: this.state.newSet,
      defaultRepMax: this.state.defaultRepMax,
      locals,
    });

    if (newSet) {
      form.sets.push(newSet);
      this.setState(
        {
          form,
          newSet: { units: newSet.units },
          isNewSetValid: false,
        },
        () => {
          focusAfterSetCommit(focusAfter);
          onCommitted?.();
        }
      );
      this.flashHighlight("highlightSet", {
        index: form.sets.length - 1,
        token: Date.now(),
      });

      // Reset the Add Set values
      const idsToReset = [
        "exerciseAddSets",
        "exerciseAddReps",
        "exerciseAddRpe",
        "exerciseAddLoad",
        "exerciseAddWeightLoad",
        "exerciseAddLoadDrop",
      ];
      idsToReset.forEach((id) => {
        if (document.getElementById(id)) {
          document.getElementById(id).value = "";
        }
      });
    }
  }

  handleRemoveSet(setIndex) {
    // Remove a set from form sets
    const form = this.state.form;
    form.sets.splice(setIndex, 1);
    this.setState({ form });
    // re-focuses on set input
    if (document.getElementById("exerciseAddSets"))
      document.getElementById("exerciseAddSets").focus();
  }

  handleUpdateSet(setIndex) {
    // Edit a form set
    const form = this.state.form;
    const { locals } = this.context;
    let editSet;
    if (
      !this.state.selectedExercise ||
      (this.state.editSet && !this.state.selectedExercise.parameters)
    ) {
      editSet = validateSet({
        exercise: form,
        set: this.state.editSet,
        defaultRepMax: this.state.defaultRepMax,
        locals,
      });
    } else if (
      this.state.selectedExercise.parameters &&
      prevalidateOtherSet(this.state.selectedExercise, this.state.editSet)
    ) {
      editSet = this.state.editSet;
      const rpeError = splitRpeText(editSet);
      if (rpeError) {
        locals.flash("error", i18n.t(`common:${rpeError}`));
        editSet = undefined;
      }
    }
    if (editSet) {
      form.sets[setIndex] = editSet;
      form.sets[setIndex].editMode = false;
      this.setState({ form, editingSet: false, error: false });
      this.flashHighlight("highlightSet", {
        index: setIndex,
        token: Date.now(),
      });
    }
    // re-focuses on set input
    if (document.getElementById("exerciseAddSets"))
      document.getElementById("exerciseAddSets").focus();
  }

  handleNewSetChange(e) {
    const newSet = this.state.newSet;
    const name = e.target.name;

    // Typing a leading +/- into the %1RM field (primary/secondary) or the
    // weight field (assistant) means the coach wants a load modifier, not a
    // rep-max percentage or weight — flip the toggler for them instead of
    // requiring a separate click.
    if (
      ["percentRepMax", "load"].includes(name) &&
      /^[+-]/.test(String(e.target.value).trim())
    ) {
      const value = e.target.value;
      newSet.loadDrop = value;
      newSet.load = 0;
      newSet.percentRepMax = 0;
      this.setState({ preferredLoadInput: "drop", newSet }, () => {
        const dropInput = document.getElementById("exerciseAddLoadDrop");
        const percentInput = document.getElementById("exerciseAddLoad");
        const weightInput = document.getElementById("exerciseAddWeightLoad");
        if (percentInput) percentInput.value = "";
        if (weightInput) weightInput.value = "";
        if (dropInput) {
          dropInput.value = value;
          dropInput.focus();
        }
      });
      return;
    }

    newSet[name] = e.target.value;
    const isAssistant = this.state.form.type === "assistant";

    // flagging if anything has changed. This is to be used in `onClose` for `Modal`
    let hasSetChanged;
    if (!this.state.hasSetChanged) hasSetChanged = true;

    // forcing user to not input decimal on sets or reps and saving the rounded value
    if (["sets"].includes(name)) {
      newSet[name] = lround(e.target.value);
      e.target.value = newSet[name];
    }

    // forcing user to not input more than two decimal on load or repMax
    if (["load", "repMaximum"].includes(name)) {
      const value = String(e.target.value);
      if (
        value.includes(".") &&
        !value.endsWith(".") &&
        value.split(".")[1].length > 2
      ) {
        e.target.value = lround(parseFloat(value), 2);
      }
    }

    if (
      ["load", "equippedRepMax", "percentRepMax", "loadDrop"].indexOf(name) > -1
    ) {
      if (String(newSet[name]).includes(",")) {
        newSet[name] = e.target.value = String(newSet[name]).replace(/,/g, ".");
      }
      // If making an equipped set, using load percentage of RM, make sure the %RM is of the equipped RM and not the default
      if (newSet.equipped && newSet.equippedRepMax && newSet.percentRepMax) {
        // no conversion needed since both equippedRepMax and load are ensured to be in KG
        newSet.load = (newSet.equippedRepMax * newSet.percentRepMax) / 100;
      }
    }

    if (["load", "equippedRepMax"].indexOf(name) > -1) {
      // enforce load and equippedRepMax to be in KG
      if ("metric" !== this.state.newSet.units[name]) {
        newSet[name] = convert(newSet[name]).from("lb").to("kg");
      }
    }

    // defaults to latest 1RM if no secondaryRM instead of 0
    const repMax = parseFloat(
      (this.state.form.type !== "primary"
        ? this.state.form.secondaryRepMaximum
        : this.state.defaultRepMax) || this.state.defaultRepMax
    );
    // remembers last value (weight or percent) and converts appropriately during toggle
    if (name === "percentRepMax") {
      newSet.load = (repMax * parseFloat(newSet[name])) / 100;
      //ensure display load is converted to the most recently selected unit when switching from percent
      document.getElementById("exerciseAddWeightLoad").value = lround(
        this.state.newSet.units.load === "metric"
          ? newSet.load
          : convert(newSet.load).from("kg").to("lb"),
        2
      );
    }
    if (name === "load" && this.state.form.type !== "assistant") {
      // no conversion needed since both defaultRepMax and load are ensured to be in KG
      newSet.percentRepMax = (parseFloat(newSet.load) / repMax) * 100;
      document.getElementById("exerciseAddLoad").value = lround(
        newSet.percentRepMax,
        2
      );
    }

    this.setState((prevState) => {
      return {
        ...prevState,
        newSet,
        isNewSetValid: prevalidateSet(newSet, isAssistant),
        hasSetChanged,
      };
    });
    return false;
  }

  handleEditSetChange(e) {
    e.preventDefault?.();
    const editSet = this.state.editSet;
    if (!editSet) throw new Error("No set to edit");
    const name = e.target.name;

    // Same leading +/- auto-switch as handleNewSetChange, for the edit row.
    // Also covers the parameterized ParameterInput's "load" field, which
    // shares this handler for assistant/custom-parameter exercises.
    if (
      ["percentRepMax", "load"].includes(name) &&
      /^[+-]/.test(String(e.target.value).trim())
    ) {
      const value = e.target.value;
      editSet.loadDrop = value;
      editSet.load = 0;
      editSet.percentRepMax = 0;
      this.setState({ preferredLoadInput: "drop", editSet }, () => {
        const dropInput =
          document.getElementById("exerciseEditLoadDrop") ||
          document.getElementById(`${LOAD}_id_parameer_edit`);
        const percentInput = document.getElementById("exerciseEditLoad");
        const weightInput = document.getElementById("exerciseEditWeightLoad");
        if (percentInput) percentInput.value = "";
        if (weightInput) weightInput.value = "";
        if (dropInput) {
          dropInput.value = value;
          dropInput.focus();
        }
      });
      return;
    }

    editSet[name] = e.target.value;
    // flagging if anything has changed. This is to be used in `onClose` for `Modal`
    let hasSetChanged;
    if (!this.state.hasSetChanged) hasSetChanged = true;

    // forcing user to not input decimal on sets or reps and saving the rounded value
    if (["sets"].includes(name)) {
      editSet[name] = lround(e.target.value);
      e.target.value = editSet[name];
    }

    // forcing user to not input more than two decimal on load or repMax
    if (["load", "percentRepMax", "repMaximum"].includes(name)) {
      const value = String(e.target.value);
      if (
        value.includes(".") &&
        !value.endsWith(".") &&
        value.split(".")[1].length > 2
      ) {
        e.target.value = lround(parseFloat(value), 2);
      }
    }

    if (
      ["load", "equippedRepMax", "percentRepMax", "loadDrop"].indexOf(name) > -1
    ) {
      if (String(editSet[name]).includes(",")) {
        editSet[name] = e.target.value = String(editSet[name]).replace(
          /,/g,
          "."
        );
      }
      // If making an equipped set, using load percentage of RM, make sure the %RM is of the equipped RM and not the default
      if (editSet.equipped && editSet.equippedRepMax && editSet.percentRepMax) {
        // no conversion needed since both equippedRepMax and load are ensured to be in KG
        editSet.load = (editSet.equippedRepMax * editSet.percentRepMax) / 100;
      }
    }

    if (
      ["load", "equippedRepMax"].indexOf(name) > -1 &&
      this.state.selectedExercise &&
      !this.state.selectedExercise.parameters
    ) {
      // enforce load and equippedRepMax to be in KG
      if ("metric" !== this.state.editSet.units[name]) {
        editSet[name] = convert(editSet[name]).from("lb").to("kg");
      }
    }

    // defaults to latest 1RM if no secondaryRM instead of 0
    const repMax = parseFloat(
      (this.state.form.type !== "primary"
        ? this.state.form.secondaryRepMaximum
        : this.state.defaultRepMax) || this.state.defaultRepMax
    );
    // remembers last value (weight or percent) and converts appropriately during toggle
    if (name === "percentRepMax") {
      editSet.load = (repMax * parseFloat(editSet[name])) / 100;
      //ensure load is converted to the most recently selected unit when switching from percent
      document.getElementById("exerciseEditWeightLoad").value = lround(
        this.state.editSet.units.load === "metric"
          ? editSet.load
          : convert(editSet.load).from("kg").to("lb"),
        2
      );
    }
    if (name === "load" && this.state.form.type !== "assistant") {
      editSet.percentRepMax = (parseFloat(editSet[name]) / repMax) * 100;
      document.getElementById("exerciseEditLoad").value = lround(
        editSet.percentRepMax,
        2
      );
    }
    this.setState({ editSet, hasSetChanged });
  }

  /**
   * Toggle the reps per side checkbox
   */
  toggleRepsPerSide() {
    this.setState((prevState) => ({
      form: { ...prevState.form, repsPerSide: !prevState.form.repsPerSide },
    }));
  }

  /**
   * Toggle the tempo checkbox
   */
  toggleTempo() {
    const { tempoChecked, form } = this.state;
    const next = !tempoChecked;
    this.setState(
      {
        tempoChecked: next,
        tempoError: false,
        form: { ...form, tempo: next ? form.tempo : "" },
      },
      () => {
        if (next && this.tempoInputRef.current) {
          this.tempoInputRef.current.focus();
        }
      }
    );
  }

  /**
   * Handle the tempo input
   */
  onTempoInput(e) {
    this.setState((prevState) => ({
      form: { ...prevState.form, tempo: e.target.value },
      tempoError: false,
    }));
  }

  handleSaveToLibrary() {
    const {
      newMovement,
      newMovementRM,
      selectInput,
      addingFromSelect,
      form,
      defaultRepMax,
    } = this.state;

    let newMovementRMValue = defaultRepMax;
    let primaryValue = form.primary;
    let typeValue = "secondary";

    if (
      this.context.locals.athlete &&
      this.activeUser?.repMaximums?.[form.primary]
    ) {
      newMovementRMValue = this.activeUser.repMaximums[form.primary];
    }

    // If its a basic exercise its assistant
    if (form.basicExerciseType === "basicExercise") {
      primaryValue = "assistant";
      typeValue = "assistant";
      newMovementRMValue = 0;
    }

    setTimeout(() => {
      if (
        (selectInput && addingFromSelect) ||
        (newMovement && form.type !== "assistant" && newMovementRM) ||
        (newMovement && form.type === "assistant")
      ) {
        this.setState({ addingMovementToLibrary: false });
      }
      this.finishSaveToLibrary(primaryValue, newMovementRMValue, typeValue);
    });
  }

  finishSaveToLibrary(primaryValue, newMovementRMValue, typeValue) {
    const { newSet, newMovement, selectInput, form } = this.state;

    this.setState({
      selectInput: "",
      newMovement: "",
      addingFromSelect: false,
      addingMovementToLibrary: false,
      form: Object.assign(form, {
        primary: primaryValue,
        secondaryRepMaximum: newMovementRMValue,
        secondary: newMovement,
        secondaryValue: newMovement,
        exerciseMovement: newMovement,
      }),
      newExerciseFields: {
        parameters: [SETS, REPS, RPEAMRAP, LOAD],
        freeFormSelected: null,
        videoUrl: "",
        videoFile: null,
        videoOriginal: "", // if there is an uploaded video
        exerciseTypeField: "",
        primaryMuscleGroup: "",
        secondaryMuscleGroup: "",
        instructions: "",
      },
      defaultRepMax: newMovementRMValue,
    });

    const units = newSet.units.defaultRepMax;
    const inputValue =
      units === "imperial"
        ? convert(form.secondaryRepMaximum).from("kg").to("lb")
        : form.secondaryRepMaximum;
    if (document.getElementById("defaultRepmaxUnitInput")) {
      document.getElementById("defaultRepmaxUnitInput").value = lround(
        inputValue,
        2
      );
    }
    const target = {
      name: "secondaryValue",
      value: newMovement || selectInput,
      primary: primaryValue,
      type: typeValue,
      newMovementRM: form.secondaryRepMaximum,
    };

    this.handleChange({ target });
  }

  handleSetEditModeToggle(editSetIndex, initialSetState) {
    // Display the text inputs for a set so you can edit it
    const form = this.state.form;
    form.sets.forEach((set, index) => {
      if (index === editSetIndex || set.editMode === true)
        form.sets[index].editMode = !set.editMode;
      // this is to rollback incomplete edit of set to editSetBackup
      if (index === editSetIndex && initialSetState)
        form.sets[editSetIndex] = initialSetState;
    });
    // backing up editSet
    const editSetBackup = initialSetState
      ? cloneDeep(this.state.editSet)
      : cloneDeep(form.sets[editSetIndex]);
    editSetBackup.editMode = false;
    const editSet = {
      units: {
        defaultRepMax: this.locals.activeUser.units,
        load: this.locals.activeUser.units,
        equippedRepMax: this.locals.activeUser.units,
      },
      ...form.sets[editSetIndex],
      idx: editSetIndex,
    };

    if (editSet.reps && editSet.repsLower) {
      editSet.reps = `${editSet.repsLower}-${editSet.reps}`;
      editSet.repsLower = null;
    }
    joinRpeForEdit(editSet);

    this.setState({
      form,
      editSet,
      editSetBackup,
      editingSet: editSet.editMode,
      error: false,
      preferredLoadInput: this.preferredLoadInputForSet(editSet),
    });
    // re-focuses on set input
    if (document.getElementById("exerciseAddSets"))
      document.getElementById("exerciseAddSets").focus();
  }

  triggerWarning(e) {
    e.preventDefault();
    this.setState({
      error: this.props.t("addExerciseModal.unsavedSetError"),
    });
  }

  /**
   * Single gate for the metadata gaps panel (`getLibraryMetadataNoticeState`).
   * Computed once per render and stored on `this.libraryMetadataNotice` so Save
   * reuses the same snapshot without a second pass.
   *
   * @returns {{ show: false } | { show: true, missingKeys: string[], record: object, libraryIndex: number, isPrimaryExercise: boolean, repMaximum: number|null }}
   */
  resolveLibraryMetadataNotice() {
    const { form, selectedExercise } = this.state;
    const hasExerciseSelection = Boolean(
      selectedExercise ||
        form.exerciseMovement ||
        (form.secondaryValue && String(form.secondaryValue).trim() !== "")
    );
    return getLibraryMetadataNoticeState(
      this.context.locals.activeUserToRequest,
      { form, hasExerciseSelection }
    );
  }

  /**
   * Phase-2 library save from the collapsible "Exercise details" panel.
   * Runs **before** the main exercise submit when `this.libraryMetadataNotice.show`.
   *
   * Transport: not a nested form submit. We inject `submitLibraryMetadata` so the panel
   * can attach `files.videoFile` and call Rill's document `fetch` (same router as library
   * modals). See `fetchLibraryMetadataAction` in submit-library-metadata-from-add-exercise.js.
   *
   * @returns {Promise<{ skipped: true } | { ok: true, payload?: object, savedMetadata?: object|null }>}
   */
  async saveLibraryMetadataIfRequested() {
    const notice = this.libraryMetadataNotice;
    if (!notice?.show) {
      return { skipped: true };
    }
    return saveLibraryMetadata({
      getSavePayload: () =>
        this.libraryMetadataPanelRef.current?.getSavePayload?.() ?? null,
      // Wired into dispatchLibrarySubmit → panel ref → fetchLibraryMetadataAction(req, …).
      // `this.context.req` is the Rill request from RillContext (browser action router).
      submitLibraryMetadata: (url, body) =>
        this.libraryMetadataPanelRef.current?.submitLibraryMetadata?.(
          url,
          body,
          this.context.req
        ),
      record: notice.record ?? {},
    });
  }

  async handleSubmit(e) {
    // Post all form data
    e.preventDefault();
    if (this.state.submited || this._submitInProgress) return;
    // A filled-in row is added as a set first, so nothing the coach typed is lost.
    if (this.state.isNewSetValid) {
      this.handleAddSet({
        preventDefault() {},
        focusAfterCommit: "none",
        onCommitted: () => this.handleSubmit({ preventDefault() {} }),
      });
      return;
    }

    if (this.state.tempoChecked && !this.state.form.tempo.trim()) {
      this.setState({ tempoError: true });
      return;
    }

    this._submitInProgress = true;

    const form = cloneDeep(this.state.form);
    if (!this.state.tempoChecked) form.tempo = null;
    const date = this.props.date;
    // get rid of cycle and add date if this is from the calendar
    if (this.props.date) {
      delete form.cycle;
      if (!this.editExerciseMode) {
        form.date = date.clone().toDate();
      } else {
        form.date = this.editExercise.date;
      }

      // CH-4004
      form.utcDate = parseInt(date.clone().format("YYYYMMDD"), 10);
    }

    //CH-980
    if (this.editExerciseMode) {
      form.date = this.editExercise.originalDate;
      // CH-4004
      form.utcDate = parseInt(
        moment.utc(this.editExercise.originalDate).format("YYYYMMDD"),
        10
      );
    }

    // Optional library save first (Rill POST to edit-secondary / update-primary when checkbox on).
    // See saveLibraryMetadataIfRequested → fetchLibraryMetadataAction.
    // The action returns the saved metadata so we can apply it to THIS exercise below
    // (the in-memory library cache is stale until refresh).
    let libraryMetadataResult;
    try {
      libraryMetadataResult = await this.saveLibraryMetadataIfRequested();
    } catch (error) {
      console.error("[AddExercise] library metadata save failed", error);
      this._submitInProgress = false;
      alert(this.props.t("addExerciseModal.metadataLibrarySaveFailed"));
      return;
    }

    this.setState({ submited: true, error: false });

    let exercise = validateExercise(form, this.context.locals);

    if (exercise?.type === "assistant" && exercise?.sets?.length > 0) {
      exercise.sets.forEach((set) => {
        set.percentRepMax = 0;
      });
    }

    const search = exercise.exerciseMovement || exercise.secondary;
    const exLibrary =
      this.context.locals.activeUserToRequest.exerciseLibrary.assistant.find(
        (elem) => elem.movement === search
      );

    if (exercise?.parameters || exLibrary?.parameters) {
      exercise = this.addExtraPropertiesToExercise(
        Object.assign(exercise, { parameters: exLibrary.parameters })
      );
    } else {
      exercise = this.addExtraPropertiesToExercise(exercise);
    }

    // Apply the just-saved library metadata to THIS exercise (addExtraPropertiesToExercise
    // read it from the stale in-memory cache). Only when the coach changed metadata.
    if (exercise && libraryMetadataResult?.savedMetadata) {
      exercise = mergeSavedMetadataIntoExercise(
        exercise,
        libraryMetadataResult.savedMetadata
      );
    }

    if (exercise) {
      const { locals } = this.context;
      let movementLibrary = {};

      if (
        locals.query?.athlete &&
        this.state.selectedExercise &&
        form.type !== "primary"
      ) {
        const libraryCoach = get(
          locals.activeUserToRequest,
          `exerciseLibrary.${form.primary}`,
          []
        );
        const existsCoach = libraryCoach.find(
          (item) => item.movement === form.secondary
        );
        if (existsCoach) {
          existsCoach.repMaximum = form.secondaryRepMaximum;
          existsCoach.primary = form.primary;
          movementLibrary = existsCoach;
        }
      }
      submit(
        `/actions/exercise/save/${
          this.state.editExerciseMode ? this.editExercise._id : ""
        }?proxyAthlete=${this.locals.query.athlete}&reqSource=${
          this.props.reqSource
        }`,
        {
          method: "POST",
          body: Object.assign(exercise, {
            movementLibrary,
            organizationCoach: this.locals.activeUserToRequest._id,
          }),
        }
      );
    }
  }

  addExtraPropertiesToExercise(exercise) {
    const { selectedExercise } = this.state;

    if (selectedExercise) {
      return {
        ...exercise,
        video: selectedExercise.video,
        instructions: selectedExercise.instructions,
        exerciseTypeField: selectedExercise.exerciseTypeField,
        primaryMuscleGroup: selectedExercise.primaryMuscleGroup,
        secondaryMuscleGroup: selectedExercise.secondaryMuscleGroup,
        parameters: selectedExercise.parameters,
      };
    } else if (exercise?.type === "primary") {
      return this.addExtraPropertiesToPrimaryExercise(exercise);
    } else {
      return exercise;
    }
  }

  addExtraPropertiesToPrimaryExercise(exercise) {
    const primaryExercises = get(
      this.context.locals.activeUserToRequest,
      "primaryExercises"
    );
    if (primaryExercises?.[exercise.primary]) {
      return {
        ...exercise,
        video: primaryExercises[exercise.primary].video,
        instructions: primaryExercises[exercise.primary].instructions,
        exerciseTypeField: primaryExercises[exercise.primary].exerciseTypeField,
        primaryMuscleGroup:
          primaryExercises[exercise.primary].primaryMuscleGroup,
        secondaryMuscleGroup:
          primaryExercises[exercise.primary].secondaryMuscleGroup,
      };
    }
    return exercise;
  }

  handleUnitChange(e, isEditSet) {
    let {
      target: { name, value },
    } = e;
    const currentSet = isEditSet ? this.state.editSet : this.state.newSet;
    if (!currentSet.units)
      currentSet.units = {
        defaultRepMax: this.locals.activeUser.units,
        load: this.locals.activeUser.units,
        equippedRepMax: this.locals.activeUser.units,
      };
    name = name.split("[")[1].split("]")[0];

    // Enforce load and equipped rep max to be the same as the user's defaults
    if (name === "load" || name === "equippedRepMax") {
      const currentUnits = currentSet.units[name] === "imperial" ? "lb" : "kg";
      if (currentSet.units[name] !== value) {
        // Only update if there is a change
        const newUnits = currentUnits === "lb" ? "kg" : "lb";

        if (name === "load") {
          const newValue = lround(
            convert(
              document.getElementById(
                isEditSet ? "exerciseEditWeightLoad" : "exerciseAddWeightLoad"
              ).value
            )
              .from(currentUnits)
              .to(newUnits),
            2
          );
          document.getElementById(
            isEditSet ? "exerciseEditWeightLoad" : "exerciseAddWeightLoad"
          ).value = newValue;
        } else {
          const newValue = lround(
            convert(document.getElementById("equippedRepMaxNew").value)
              .from(currentUnits)
              .to(newUnits),
            2
          );
          document.getElementById("equippedRepMaxNew").value = newValue;
        }
      }
    }
    // Update displayed default rep max value when changing units
    if (name === "defaultRepMax") {
      const currentUnits =
        currentSet.units.defaultRepMax === "imperial" ? "lb" : "kg";
      if (currentSet.units.defaultRepMax !== value) {
        // Only update if there is a change
        const newUnits = currentUnits === "lb" ? "kg" : "lb";
        const newRepMax = this.locals.activeUserToRequest.round
          ? lround(
              convert(document.getElementById("defaultRepmaxUnitInput").value)
                .from(currentUnits)
                .to(newUnits),
              0
            )
          : lround(
              convert(document.getElementById("defaultRepmaxUnitInput").value)
                .from(currentUnits)
                .to(newUnits),
              2
            );
        document.getElementById("defaultRepmaxUnitInput").value = newRepMax;
      }
    }

    // Update displayed secondary rep max value when changing units
    if (name === "newRepMax") {
      name = "defaultRepMax"; // reusing defaultRepMax stored on state.
      const currentUnits =
        currentSet.units.defaultRepMax === "imperial" ? "lb" : "kg";
      if (currentSet.units.defaultRepMax !== value) {
        // Only update if there is a change
        const newUnits = currentUnits === "lb" ? "kg" : "lb";
        const newRepMax = lround(
          convert(document.getElementById("newRMUnitInput").value)
            .from(currentUnits)
            .to(newUnits),
          2
        );
        document.getElementById("newRMUnitInput").value = newRepMax;

        // Setting the value for the units and updating newMovementRM
        currentSet.units.defaultRepMax = value;
        this.setState({ newMovementRM: newRepMax });
      }
    }

    currentSet.units[name] = value;
    if (!isEditSet) this.setState({ newSet: currentSet });
    if (isEditSet) this.setState({ editSet: currentSet });

    // hack attempt to solve the radio button firing once issue. See https://github.com/facebook/react/issues/4930
    // although there is no preventdefault() here. Explicitly returning true may be the answer
    return true;
  }

  confirmLeavePage(e) {
    const result = confirm(this.props.t("addExerciseModal.confirmLeavePage"));
    if (!result) e.preventDefault();
  }

  /**
   * Pick from "Select an existing exercise". Added sets are cleared only
   * after the coach confirms; cancel leaves the current exercise in place.
   */
  handleExerciseOption(option) {
    if (!option) return;
    option.name = "exerciseMovement";
    if (shouldConfirmExerciseSwitch(this.state.form, option)) {
      if (!confirm(this.props.t("addExerciseModal.confirmSwitchExercise"))) {
        return;
      }
      this.state.form.sets = [];
      this.setState({ editingSet: false, editSet: null });
    }
    this.handleMovementChange({ target: option });
    this.focusSetEntry();
  }

  /** Move focus to the first set field after an exercise is chosen. */
  focusSetEntry() {
    window.setTimeout(() => {
      const sets = document.getElementById("exerciseAddSets");
      if (sets) {
        sets.focus();
        return;
      }
      const first = document.querySelector(
        ".Add-Exercise .add-set-wrapper input:not([type='hidden']):not([type='radio']), .Add-Exercise .add-set-wrapper select, .Add-Exercise .add-set-wrapper textarea"
      );
      if (first) first.focus();
    }, 0);
  }

  handleMovementChange(e) {
    // resetting newSet.load so user inputs it fresh to avoid confusion of carried over values
    const { form, newSet } = this.state;
    const { value, label, type } = e.target;

    if (form?.type !== "assistant") {
      newSet.load = newSet.percentRepMax = 0;

      document.getElementById("exerciseAddWeightLoad").value = newSet.load;
      document.getElementById("exerciseAddLoad").value = newSet.percentRepMax;
    }

    this.setState({
      form: Object.assign(form, {
        secondaryValue: value,
        secondary: value.split("{")[0],
        selectedLabel: label,
      }),
      newSet,
      // A new movement may not support the previously selected load mode
      // (e.g. assistant exercises have no %1RM), so re-derive it for the type.
      preferredLoadInput: this.preferredLoadInputForType(type),
    });

    this.handleChange(e);
  }

  handleCancel(e) {
    // Push the cancel button, confirm that work will be lost if any work has been done
    e.preventDefault();
    if (
      !this.state.hasSetChanged ||
      confirm(this.props.t("addExerciseModal.confirmLeavePage"))
    ) {
      submit(`/actions/refresh/`, {
        method: "GET",
        body: { modal: "", editMode: "", insertTarget: "", insertPosition: "" },
      });
    }
  }

  /* ---------------- workout queue ---------------- */

  persistQueueState(items, workoutName, note) {
    if (this.editWorkout) {
      this.persistEditState(items, note);
      return;
    }
    persistQueue(
      this.queueStorageKey,
      items ?? this.state.queueItems,
      workoutName ?? this.state.queueWorkoutName,
      // Only overridden by handleQueueNoteChange, which always passes the
      // current (possibly "") text — every other caller keeps whatever note
      // state already holds instead of clobbering it with undefined.
      note ?? this.state.queueNote
    );
  }

  /** Reset the form so the coach can build the next exercise of the queue. */
  resetFormForNextExercise(callback) {
    const units = {
      defaultRepMax: this.locals.activeUser.units,
      load: this.locals.activeUser.units,
      equippedRepMax: this.locals.activeUser.units,
    };
    this.setState({
      form: {
        primary: "squat",
        secondary: "",
        secondaryValue: "",
        exerciseMovement: "",
        sets: [],
        type: "secondary",
        repMaximums: get(this.activeUser, "repMaximums", {
          squat: 0,
          bench: 0,
          deadlift: 0,
        }),
        secondaryRepMaximum: 0,
        cycle: this.state.form.cycle,
        notes: "",
        repsPerSide: false,
        tempo: "",
        basicExerciseType: "basicExercise",
        selectedLabel: "",
      },
      selectedExercise: undefined,
      selectInput: "",
      tempoChecked: false,
      tempoError: false,
      newSet: { units },
      isNewSetValid: false,
      hasSetChanged: false,
      defaultRepMax: get(this.activeUser, "repMaximums.squat", 0),
      error: false,
      // New card always starts on squat (a %1RM-eligible primary), so the
      // org/user default load mode is always valid here.
      preferredLoadInput: this.defaultPreferredLoadInput(),
    });
    // Runs once the reset above has been applied.
    if (callback) this.setState({}, callback);

    // Uncontrolled inputs are not driven by state — clear them by hand.
    [
      "exerciseAddSets",
      "exerciseAddReps",
      "exerciseAddRpe",
      "exerciseAddLoad",
      "exerciseAddWeightLoad",
      "exerciseAddLoadDrop",
    ].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.value = "";
    });
    const notes = document.querySelector(
      '#add-exercise textarea[name="notes"]'
    );
    if (notes) notes.value = "";
  }

  /**
   * Validate the current form and push it into the queue (or write it back
   * to the card being edited). Mirrors handleSubmit's pipeline but never
   * posts — the single POST happens in handleSaveQueue.
   */
  async handleAddNextExercise(e, { onQueued } = {}) {
    e.preventDefault();
    // A filled-in row is added as a set first, so nothing the coach typed is lost.
    if (this.state.isNewSetValid) {
      this.handleAddSet({
        preventDefault() {},
        focusAfterCommit: "none",
        onCommitted: () =>
          this.handleAddNextExercise({ preventDefault() {} }, { onQueued }),
      });
      return;
    }
    const built = await this.buildQueuedExercise();
    if (!built) return;
    const { rawForm, exercise } = built;

    const { queueItems, editingQueueIndex } = this.state;
    let nextItems;
    let committedId;
    if (editingQueueIndex !== null && queueItems[editingQueueIndex]) {
      committedId = queueItems[editingQueueIndex].id;
      // Update in place: keep the card's position and superset link.
      nextItems = queueItems.map((item, index) =>
        index === editingQueueIndex ? { ...item, rawForm, exercise } : item
      );
    } else {
      committedId = generateQueueItemId();
      nextItems = [
        ...queueItems,
        {
          kind: "exercise",
          id: committedId,
          rawForm,
          exercise,
          linkedToPrev: false,
        },
      ];
    }

    this.persistQueueState(nextItems);
    this.setState({ queueItems: nextItems, editingQueueIndex: null });
    this.flashHighlight("highlightQueueId", committedId);
    // onQueued runs once the form is reset too, so a follow-up save sees an
    // empty form instead of queueing the same exercise again.
    this.resetFormForNextExercise(() => onQueued?.());
    focusExercisePicker();
  }

  /**
   * Validate the form into `{ rawForm, exercise }` (the queue card's editor
   * snapshot and save payload), or null when it is invalid. Mirrors
   * handleSubmit's pipeline but never posts.
   */
  async buildQueuedExercise() {
    if (this.state.tempoChecked && !this.state.form.tempo.trim()) {
      this.setState({ tempoError: true });
      return null;
    }

    // Snapshot to reload into the form when the card is edited later.
    const rawForm = cloneDeep(this.state.form);
    rawForm.tempoChecked = this.state.tempoChecked;

    const form = cloneDeep(this.state.form);
    if (!this.state.tempoChecked) form.tempo = null;
    if (this.props.date) delete form.cycle;

    // Optional library save first (same phase-2 flow as handleSubmit).
    let libraryMetadataResult;
    try {
      libraryMetadataResult = await this.saveLibraryMetadataIfRequested();
    } catch (error) {
      console.error("[AddExercise] library metadata save failed", error);
      alert(this.props.t("addExerciseModal.metadataLibrarySaveFailed"));
      return null;
    }

    let exercise = validateExercise(form, this.context.locals, {
      refreshOnError: false,
    });
    if (!exercise) return null;

    if (exercise.type === "assistant" && exercise.sets?.length > 0) {
      exercise.sets.forEach((set) => {
        set.percentRepMax = 0;
      });
    }

    const search = exercise.exerciseMovement || exercise.secondary;
    const exLibrary =
      this.context.locals.activeUserToRequest.exerciseLibrary.assistant.find(
        (elem) => elem.movement === search
      );
    if (exercise?.parameters || exLibrary?.parameters) {
      exercise = this.addExtraPropertiesToExercise(
        Object.assign(exercise, { parameters: exLibrary.parameters })
      );
    } else {
      exercise = this.addExtraPropertiesToExercise(exercise);
    }
    if (exercise && libraryMetadataResult?.savedMetadata) {
      exercise = mergeSavedMetadataIntoExercise(
        exercise,
        libraryMetadataResult.savedMetadata
      );
    }
    if (!exercise) return null;

    // Coach updating an athlete's library rep max (same as handleSubmit).
    const { locals } = this.context;
    let movementLibrary = {};
    if (
      locals.query?.athlete &&
      this.state.selectedExercise &&
      this.state.form.type !== "primary"
    ) {
      const libraryCoach = get(
        locals.activeUserToRequest,
        `exerciseLibrary.${this.state.form.primary}`,
        []
      );
      const existsCoach = libraryCoach.find(
        (item) => item.movement === this.state.form.secondary
      );
      if (existsCoach) {
        existsCoach.repMaximum = this.state.form.secondaryRepMaximum;
        existsCoach.primary = this.state.form.primary;
        movementLibrary = existsCoach;
      }
    }
    exercise.movementLibrary = movementLibrary;
    return { rawForm, exercise };
  }

  /** Load a queued exercise back into the form for editing. */
  handleEditQueueItem(index) {
    const item = this.state.queueItems[index];
    if (!isQueueExercise(item)) return;

    // Edit Workout cards start without a form snapshot: build it from the
    // saved exercise plus any draft (bulk changes).
    const rawForm =
      item.rawForm ??
      exerciseToRawForm(
        { ...item.original, ...(item.exercise || {}) },
        this.activeUser.exerciseLibrary
      );
    const { tempoChecked = false, ...form } = cloneDeep(rawForm);
    const search = form.exerciseMovement || form.secondary;
    const selectedExercise = get(
      this.context.locals.activeUserToRequest,
      `exerciseLibrary.${form.primary}`,
      []
    ).find((elem) => elem.movement === search);

    this.setState(
      {
        form,
        tempoChecked,
        tempoError: false,
        selectedExercise,
        editingQueueIndex: index,
        isNewSetValid: false,
        defaultRepMax: get(
          form,
          `repMaximums.${form.primary}`,
          this.state.defaultRepMax
        ),
        preferredLoadInput: this.preferredLoadInputForType(form.type),
        editNewIndex: null,
      },
      () => {
        if (!this.editWorkout) return;
        this.loadedEditForm = this.editFormSnapshot();
        // The footer and Save read the snapshot: render once it is set.
        this.forceUpdate();
      }
    );

    const notes = document.querySelector(
      '#add-exercise textarea[name="notes"]'
    );
    if (notes) notes.value = form.notes || "";
  }

  cancelQueueEdit() {
    this.setState({ editingQueueIndex: null });
    this.resetFormForNextExercise();
  }

  handleRemoveQueueItem(index) {
    const { queueItems, editingQueueIndex } = this.state;
    if (this.editWorkout) {
      const item = queueItems[index];
      // Saved exercises are only marked (deleted on save, Restore undoes it);
      // sections merge into the one above; new cards just go.
      if (item.kind === "exercise" && item.serverId) {
        this.setEditItems(
          sanitizeQueueLinks(
            queueItems.map((other, i) =>
              i === index ? { ...other, removed: !other.removed } : other
            )
          )
        );
        return;
      }
      this.setEditItems(
        sanitizeQueueLinks(queueItems.filter((_item, i) => i !== index))
      );
      if (index === editingQueueIndex) {
        this.loadedEditForm = null;
        this.resetFormForNextExercise();
      }
      return;
    }
    const removingEdited = editingQueueIndex === index;
    let nextEditing = editingQueueIndex;
    if (removingEdited) nextEditing = null;
    else if (editingQueueIndex !== null && index < editingQueueIndex)
      nextEditing = editingQueueIndex - 1;

    const nextItems = sanitizeQueueLinks(
      queueItems.filter((_item, i) => i !== index)
    );
    this.persistQueueState(nextItems);
    this.setState({ queueItems: nextItems, editingQueueIndex: nextEditing });
    if (removingEdited) this.resetFormForNextExercise();
  }

  handleToggleQueueLink(index) {
    const nextItems = this.state.queueItems.map((item, i) =>
      i === index ? { ...item, linkedToPrev: !item.linkedToPrev } : item
    );
    this.persistQueueState(nextItems);
    this.setState({ queueItems: nextItems });
  }

  /**
   * Drag-and-drop reorder from the panel. `reordered` already keeps superset
   * blocks atomic (see reorderQueuePreservingLinks): dragging one member
   * carries its whole superset along, same as the calendar's DayEditor.
   * Reordering invalidates the edit pointer, so editing (if any) is
   * cancelled.
   */
  handleQueueItemsChange(reordered) {
    if (this.editWorkout) {
      // Drafts live on the cards, so the open card just follows its id.
      this.setEditItems(sanitizeQueueLinks(reordered));
      return;
    }
    const wasEditing = this.state.editingQueueIndex !== null;
    const nextItems = sanitizeQueueLinks(reordered);
    this.persistQueueState(nextItems);
    this.setState({ queueItems: nextItems, editingQueueIndex: null });
    if (wasEditing) this.resetFormForNextExercise();
  }

  handleAddQueueSection(position, name) {
    const { queueItems, editingQueueIndex } = this.state;
    const nextItems = [...queueItems];
    nextItems.splice(position, 0, {
      kind: "section",
      id: generateQueueItemId(),
      name,
    });
    const sanitized = sanitizeQueueLinks(nextItems);
    const nextEditing =
      editingQueueIndex !== null && position <= editingQueueIndex
        ? editingQueueIndex + 1
        : editingQueueIndex;
    this.persistQueueState(sanitized);
    this.setState({ queueItems: sanitized, editingQueueIndex: nextEditing });
  }

  handleRenameQueueSection(index, name) {
    const nextItems = this.state.queueItems.map((item, i) =>
      i === index ? { ...item, name } : item
    );
    this.persistQueueState(nextItems);
    this.setState({ queueItems: nextItems });
  }

  handleQueueWorkoutNameChange(value) {
    this.persistQueueState(this.state.queueItems, value);
    this.setState({ queueWorkoutName: value });
  }

  handleQueueNoteChange(value) {
    this.persistQueueState(undefined, undefined, value);
    this.setState({ queueNote: value, queueNoteDirty: true });
  }

  handleToggleQueueNote() {
    this.setState((prev) => ({ queueNoteExpanded: !prev.queueNoteExpanded }));
  }

  /** Cancel button in queue mode: exit card edit, or discard the queue. */
  handleQueueCancel(e) {
    e.preventDefault();
    if (this.state.editingQueueIndex !== null) {
      this.cancelQueueEdit();
      return;
    }
    if (this.state.queueItems.length || this.state.queueNoteDirty) {
      const count = countQueueExercises(this.state.queueItems);
      if (!confirm(this.props.t("workoutQueue.confirmDiscardQueue", { count })))
        return;
      clearQueue(this.queueStorageKey);
      submit("/actions/refresh/", {
        method: "GET",
        body: { modal: "", editMode: "", insertTarget: "", insertPosition: "" },
      });
      return;
    }
    this.handleCancel(e);
  }

  /** The single save: POST the whole queue to the batch action. */
  handleSaveQueue() {
    const { queueItems, queueSaving, queueNote, queueNoteDirty } = this.state;
    const hasNoteToSave = queueNoteDirty && !!queueNote.trim();
    if (queueSaving) return;
    // The exercise being built (or a filled-in row) is queued first, so the
    // saved workout includes everything the coach typed.
    if (this.state.isNewSetValid || this.state.form.sets.length > 0) {
      this.handleAddNextExercise(
        { preventDefault() {} },
        { onQueued: () => this.handleSaveQueue() }
      );
      return;
    }
    // A note-only save (no exercises queued) is legal — the queue's
    // "Instructions (Optional)" row works even with an empty queue.
    if (!queueItems.some(isQueueExercise) && !hasNoteToSave) return;

    this.setState({ queueSaving: true });

    const items = queueItems.map((item) =>
      isQueueExercise(item)
        ? {
            kind: "exercise",
            ...item.exercise,
            linkedToPrev: !!item.linkedToPrev,
          }
        : { kind: "section", name: item.name }
    );
    const target = this.props.date
      ? { date: this.props.date.clone().toDate() }
      : { cycle: this.state.form.cycle };

    submit(
      `/actions/exercise/save-workout-queue?proxyAthlete=${this.locals.query.athlete}&reqSource=${this.props.reqSource}`,
      {
        method: "POST",
        body: {
          target,
          workoutName: this.props.date ? "" : this.state.queueWorkoutName,
          items,
          insert: this.insertSession
            ? {
                targetExerciseId: this.insertSession.target._id,
                position: this.state.insertPosition,
              }
            : undefined,
          // undefined when the note row was never touched this session, so
          // the server leaves any existing note alone instead of resending
          // (and no-opping against) stale prefilled text on every save.
          workoutNote: queueNoteDirty ? queueNote : undefined,
          queueStorageKey: this.queueStorageKey,
          organizationCoach: this.locals.activeUserToRequest._id,
        },
      }
    );
  }

  /** "Adding to" label in the queue panel header. */
  queueDestinationLabel() {
    if (this.props.date) return this.props.date.format("ddd, MMM D, YYYY");
    const cycle = this.state.form.cycle;
    return this.props.t("workoutQueue.destinationProgram", {
      week: cycle?.programWeek,
      workout: cycle?.programSession,
    });
  }

  handleReorderSet(step, setIndex) {
    const form = this.state.form;
    const sets = form.sets;
    // splice out the set from the array of sets
    const movedSet = sets.splice(setIndex, 1)[0];
    // put the set back in its new place
    // TODO: validate new place is possible -1 < possible < sets.length
    sets.splice(setIndex + step, 0, movedSet);
    // ensure form is updated. Ideally the below line should be implicit but just making sure.
    form.sets = sets;

    this.setState({ form });
  }

  buildMovementOptionsArray() {
    const { activeUserToRequest } = this.context.locals;
    const {
      squat,
      bench,
      deadlift,
      assistant,
      clean = [],
      jerk = [],
      snatch = [],
      overheadPress = [],
      other = [],
    } = activeUserToRequest.exerciseLibrary;
    const t = this.props.t;
    const movementOptions = [
      {
        value: "squat",
        label: t("addExerciseModal.squat"),
        movementPrimary: "squat",
        type: "primary",
      },
      {
        value: "bench",
        label: t("addExerciseModal.bench"),
        movementPrimary: "bench",
        type: "primary",
      },
      {
        value: "deadlift",
        label: t("addExerciseModal.deadlift"),
        movementPrimary: "deadlift",
        type: "primary",
      },
      {
        value: "clean",
        label: t("addExerciseModal.clean"),
        movementPrimary: "clean",
        type: "primary",
      },
      {
        value: "jerk",
        label: t("addExerciseModal.jerk"),
        movementPrimary: "jerk",
        type: "primary",
      },
      {
        value: "snatch",
        label: t("addExerciseModal.snatch"),
        movementPrimary: "snatch",
        type: "primary",
      },
      {
        value: "overheadPress",
        label: t("addExerciseModal.overheadPress"),
        movementPrimary: "overheadPress",
        type: "primary",
      },
    ];

    squat.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{squat}`,
        label: `${item.movement} (${t("addExerciseModal.squat")})`,
        movementPrimary: "squat",
        type: "secondary",
      });
    });
    bench.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{bench}`,
        label: `${item.movement} (${t("addExerciseModal.bench")})`,
        movementPrimary: "bench",
        type: "secondary",
      });
    });
    deadlift.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{deadlift}`,
        label: `${item.movement} (${t("addExerciseModal.deadlift")})`,
        movementPrimary: "deadlift",
        type: "secondary",
      });
    });
    clean.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{clean}`,
        label: `${item.movement} (${t("addExerciseModal.clean")})`,
        movementPrimary: "clean",
        type: "secondary",
      });
    });
    jerk.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{jerk}`,
        label: `${item.movement} (${t("addExerciseModal.jerk")})`,
        movementPrimary: "jerk",
        type: "secondary",
      });
    });
    snatch.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{snatch}`,
        label: `${item.movement} (${t("addExerciseModal.snatch")})`,
        movementPrimary: "snatch",
        type: "secondary",
      });
    });
    overheadPress.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{overheadPress}`,
        label: `${item.movement} (${t("addExerciseModal.overheadPress")})`,
        movementPrimary: "overheadPress",
        type: "secondary",
      });
    });
    other.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{other}`,
        label: item.movement,
        movementPrimary: "other",
        type: "secondary",
      });
    });
    assistant.forEach((item) => {
      movementOptions.push({
        value: `${item.movement}{assistant}`,
        label: item.movement,
        movementPrimary: "assistant",
        type: "assistant",
      });
    });

    return movementOptions;
  }

  handleNewFieldsInputChange(event) {
    const { value, name } = event.target;

    this.setState((prevState) => ({
      newExerciseFields: { ...prevState.newExerciseFields, [name]: value },
    }));
  }

  handleParameterInputChange(event) {
    let { value, name } = event.target;

    // Same leading +/- auto-switch as handleNewSetChange/handleEditSetChange,
    // for exercises with custom parameters (e.g. assistant exercises).
    if (name === "load" && /^[+-]/.test(String(value).trim())) {
      this.setState(
        (prevState) => ({
          preferredLoadInput: "drop",
          parametersForm: {
            ...prevState.parametersForm,
            load: 0,
            loadDrop: value,
          },
        }),
        () => {
          const dropInput = document.getElementById(`${LOAD}_id_parameter`);
          if (dropInput) {
            dropInput.value = value;
            dropInput.focus();
          }
          const { selectedExercise, parametersForm } = this.state;
          this.setState({
            isNewSetValid: prevalidateOtherSet(
              selectedExercise,
              parametersForm
            ),
          });
        }
      );
      return;
    }

    if (["load", "loadDrop"].indexOf(name) > -1) {
      if (String(value).includes(",")) {
        value = event.target.value = String(value).replace(/,/g, ".");
      }
    }

    this.setState(
      (prevState) => ({
        parametersForm: { ...prevState.parametersForm, [name]: value },
      }),
      () => {
        const { selectedExercise, parametersForm } = this.state;

        //validate set
        this.setState({
          isNewSetValid: prevalidateOtherSet(selectedExercise, parametersForm),
        });
      }
    );
  }

  validateAddSecondary() {
    const { newMovement } = this.state;

    return newMovement;
  }

  validateAddSecondaryParameters() {
    const {
      newExerciseFields: { parameters },
      form: { basicExerciseType },
    } = this.state;

    return (
      basicExerciseType === "powerliftingExercise" ||
      parameters.findIndex((elem) => elem !== NONE) > -1
    );
  }

  render() {
    const { editExercise } = this;
    const { locals } = this.context;
    const {
      activeUser,
      activeUserToRequest,
      organizationPermissions: globalOrganizationPermissions = [],
      query,
    } = locals;
    const {
      defaultRepMax,
      editExerciseMode,
      preferredLoadInput,
      form,
      hasSetChanged,
      updateEnabled,
      newSet,
      updateRMPrompt,
      isNewSetValid,
      editSet,
      editSetBackup,
      selectInput,
      addingFromSelect,
      addingMovementToLibrary,
      newExerciseFields: {
        freeFormSelected,
        parameters,
        videoFile,
        videoOriginal,
        videoUrl,
        exerciseTypeField,
        primaryMuscleGroup,
        secondaryMuscleGroup,
        instructions,
      },
      selectedExercise,
      newMovement,
      tempoChecked,
      tempoError,
    } = this.state;
    const {
      primary,
      sets,
      secondaryValue,
      secondaryRepMaximum,
      type: exerciseType,
      basicExerciseType,
      selectedLabel,
    } = form;
    const { organizationPermissions = [], organizationId } = activeUser;
    const isAssistant = exerciseType === "assistant";
    const movementOptions = this.buildMovementOptionsArray();
    // Set the value for movement name depending if addingFromSelect or typing new value
    const movementName = addingFromSelect
      ? { value: selectInput }
      : { value: newMovement };
    let isSecondaryDisabled = true;

    if (addingMovementToLibrary)
      isSecondaryDisabled = !this.validateAddSecondary();

    const intensityMeasure =
      get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
        ? "RPE"
        : "RiR";
    const isRiR = intensityMeasure === "RiR";

    const userOrganizationPermission =
      organizationPermissions.length > 0
        ? getPermissionNamesByIds(
            globalOrganizationPermissions,
            organizationPermissions
          )
        : [""];

    const userOrganizationCanAccessToExerciseLibrary =
      !organizationId ||
      (!!organizationId &&
        userOrganizationPermission.includes("Exercise Library"));

    const showRM =
      (this.locals.pathname.includes("athlete-portal/calendar") &&
        secondaryValue !== "" &&
        form?.type !== "assistant") ||
      (this.locals.athlete &&
        !this.locals.pathname.includes("athlete-portal/calendar") &&
        this.locals.pathname.includes("athlete-portal/programs/edit-program") &&
        secondaryValue !== "" &&
        form?.type !== "assistant") ||
      this.locals.pathname.includes("/dashboard/calendar");
    const isEditing = !!this.state.editSet && !!this.state.editingSet;
    const queueHasExercises = this.state.queueItems.some(isQueueExercise);
    const rowEmpty = isSetRowEmpty(
      newSet,
      this.state.parametersForm,
      selectedExercise
    );
    const exerciseHasSets = sets.length > 0;
    const buttonStates = getButtonStates({
      rowValid: isNewSetValid,
      rowEmpty,
      exerciseHasSets,
      queueHasExercises,
      editingSet: !!this.state.editingSet,
      queueEnabled: this.queueEnabled,
    });
    // "Save workout · N exercises" counts the exercise Save will auto-commit.
    const pendingExerciseCount = exerciseHasSets || isNewSetValid ? 1 : 0;
    const saveCount =
      countQueueExercises(this.state.queueItems) + pendingExerciseCount;
    const exerciseName =
      selectedLabel ||
      secondaryValue.split("{")[0] ||
      this.props.t(`addExerciseModal.${primary}`, { defaultValue: primary });
    const newMovementRMValue =
      form.basicExerciseType === "basicExercise" ? 0 : defaultRepMax;
    const editChanges = this.editWorkout ? this.editChanges() : null;
    const openEditItem = this.editWorkout
      ? this.state.queueItems[this.state.editingQueueIndex]
      : null;
    const editReadOnly = Boolean(openEditItem?.locked || openEditItem?.removed);

    // Get the library metadata notice state (cached for Save — see resolveLibraryMetadataNotice).
    this.libraryMetadataNotice = this.resolveLibraryMetadataNotice();
    const libraryMetadataNotice = this.libraryMetadataNotice;

    const insertView = this.insertSession
      ? describeInsert({
          exercises: this.insertSession.exercises,
          sections: this.insertSession.sections,
          targetId: this.insertSession.target._id,
          position: this.state.insertPosition,
          queueItems: this.state.queueItems,
        })
      : null;
    const insertProps = insertView
      ? {
          position: this.state.insertPosition,
          onPositionChange: this.handleInsertPositionChange,
          targetName: getExerciseName(this.insertSession.target),
          view: insertView,
        }
      : null;

    return (
      <Modal
        toClear={[
          "movement",
          "editMode",
          "programSession",
          "programWeek",
          "secondaryAdded",
          "addType",
          "exercise",
          "complex",
          "exercises",
          "insertTarget",
          "insertPosition",
        ]}
        onClose={
          this.editWorkout
            ? (e) => this.handleEditClose(e)
            : hasSetChanged || this.state.editingQueueIndex !== null
              ? this.confirmLeavePage
              : null
        }
        title={
          this.editWorkout
            ? this.props.t("editWorkout.title")
            : editExerciseMode
              ? this.props.t("addExerciseModal.editExercise")
              : this.props.t("addExerciseModal.addExercise")
        }
        className="Add-Exercise"
        aside={
          this.queueEnabled && !addingMovementToLibrary ? (
            <WorkoutQueuePanel
              items={this.state.queueItems}
              editingIndex={this.state.editingQueueIndex}
              destinationLabel={this.queueDestinationLabel()}
              edit={
                editChanges && {
                  changes: editChanges,
                  athleteName: this.props.date ? locals.athlete?.name : null,
                  onBulkApply: (fields, scope) =>
                    this.handleBulkApply(fields, scope),
                }
              }
              showWorkoutName={!this.props.date && !this.editWorkout}
              workoutName={this.state.queueWorkoutName}
              activeUser={{
                units: this.locals.activeUser.units,
                round: activeUserToRequest.round,
              }}
              intensityMeasure={intensityMeasure}
              isRiR={isRiR}
              saving={this.state.queueSaving}
              editingSet={this.state.editingSet}
              highlightId={this.state.highlightQueueId}
              saveVariant={buttonStates.save}
              saveCount={saveCount}
              insert={insertProps}
              onWorkoutNameChange={this.handleQueueWorkoutNameChange}
              onItemsChange={this.handleQueueItemsChange}
              onToggleLink={this.handleToggleQueueLink}
              onAddSection={this.handleAddQueueSection}
              onRenameSection={this.handleRenameQueueSection}
              onEdit={
                this.editWorkout
                  ? (index) => this.selectEditItem(index)
                  : this.handleEditQueueItem
              }
              onRemove={this.handleRemoveQueueItem}
              onSave={
                this.editWorkout
                  ? () => this.handleSaveWorkoutEdit()
                  : this.handleSaveQueue
              }
              noteValue={this.state.queueNote}
              noteExpanded={this.state.queueNoteExpanded}
              onNoteToggle={this.handleToggleQueueNote}
              onNoteChange={this.handleQueueNoteChange}
            />
          ) : null
        }
      >
        {!addingMovementToLibrary && !editExerciseMode && !this.editWorkout && (
          <AddExerciseTabs addType={query.addType} />
        )}
        {this.state.bulkToast &&
          // Outside the modal: its label styles would hide the toast text.
          createPortal(
            <div aria-live="polite">
              <Toast open type="success" message={this.state.bulkToast} />
            </div>,
            document.body
          )}
        {query.addType === "complex" ? (
          <AddComplex
            canAccessLibrary={userOrganizationCanAccessToExerciseLibrary}
            date={this.props.date}
          />
        ) : query.addType === "metcon" ? (
          <AddMetcon
            date={this.props.date}
            locals={locals}
            editExercise={this.editExercise}
          />
        ) : addingMovementToLibrary ? (
          <form
            onSubmit={(e) => {
              if (!this.validateAddSecondaryParameters()) {
                alert(this.props.t("addExerciseModal.specifyParameter"));
                e.preventDefault();
                return;
              }

              this.handleSaveToLibrary();
            }}
            id="add-exercise-form"
            action="/actions/user/add-secondary"
            encType="multipart/form-data"
            method="POST"
            data-noreset
          >
            <input
              type="hidden"
              name="movement"
              value={addingFromSelect ? selectInput : newMovement}
            />
            <input
              type="hidden"
              name="repMaximum"
              value={
                newSet.units.defaultRepMax === "imperial"
                  ? convert(parseFloat(newMovementRMValue)).from("lb").to("kg")
                  : parseFloat(newMovementRMValue)
              }
            />
            <AddLibraryExercise
              basicExerciseType={basicExerciseType}
              handleChange={this.handleChange}
              movementName={movementName}
              handleNewMovementChange={(newMovement) =>
                this.setState({
                  addingFromSelect: false,
                  newMovement,
                })
              }
              primary={primary}
              videoUrl={videoUrl}
              videoFile={videoFile}
              videoOriginal={videoOriginal}
              exerciseTypeField={exerciseTypeField}
              primaryMuscleGroup={primaryMuscleGroup}
              secondaryMuscleGroup={secondaryMuscleGroup}
              instructions={instructions}
              handleNewFieldsInputChange={this.handleNewFieldsInputChange}
              parameters={parameters}
              freeFormSelected={freeFormSelected}
              updateOtherExerciseParameters={this.updateOtherExerciseParameters}
              intensityMeasure={intensityMeasure}
              isRiR={isRiR}
              onDiscard={() =>
                this.setState({
                  addingMovementToLibrary: false,
                })
              }
              isSecondaryDisabled={isSecondaryDisabled}
            />
          </form>
        ) : (
          <form
            onSubmit={(e) => this.handleSubmit(e)}
            onKeyDown={preventImplicitEnterSubmit}
            id="add-exercise"
          >
            <div
              className={`body${editReadOnly ? " edit-workout-readonly" : ""}`}
            >
              {this.editWorkout && this.renderEditNav(openEditItem)}
              <div className="form-row">
                <div className="form-row-item basis-3/5">
                  <label>
                    {this.props.t("addExerciseModal.selectExistingExercise")}
                  </label>
                  <SearchDropdown
                    options={movementOptions}
                    placeholder={this.props.t(
                      "addExerciseModal.selectExercisePlaceholder"
                    )}
                    handleChange={this.handleExerciseOption}
                    value={selectedLabel || secondaryValue.split("{")[0]}
                    autoFocus={!editExerciseMode}
                    typeAhead
                    summaryId={ADD_EXERCISE_PICKER_ID}
                  />
                </div>

                <div
                  className={
                    showRM
                      ? `form-row-item basis-2/5${
                          updateEnabled || updateRMPrompt ? " hide-units" : ""
                        }`
                      : "hidden"
                  }
                >
                  <label htmlFor="defaultRepMax">
                    {this.props.t("addExerciseModal.repMaxLabel")}
                  </label>
                  <UnitInput
                    tabIndex="0"
                    metric="kg"
                    imperial="lb"
                    name="defaultRepMax"
                    id="defaultRepmaxUnitInput"
                    defaultValue={
                      !this.locals.athlete
                        ? 100
                        : lround(
                            defaultRepMax !== 0 && secondaryRepMaximum === 0
                              ? defaultRepMax
                              : secondaryRepMaximum,
                            2
                          )
                    }
                    needToRound={this.locals.activeUserToRequest.round}
                    onProgramEditor={true}
                    units={newSet.units.defaultRepMax}
                    handleUnitChange={this.handleUnitChange}
                    type="number"
                    min={0}
                    step=".01"
                    onInput={this.handleRMChange}
                  />
                  {updateEnabled && (
                    <div className="edit-confirm">
                      <button
                        onClick={(e) => this.handleRMUpdate(e)}
                        data-button-type="primary"
                        type="button"
                      >
                        {this.props.t("common:save")}
                      </button>
                      <button
                        onClick={(e) => this.handleRMDismiss(e)}
                        data-button-type="secondary"
                        type="button"
                      >
                        <SvgIcon src="/img/new-icons/red-cross.svg" />
                      </button>
                    </div>
                  )}
                  {updateRMPrompt && (
                    <div className="edit-confirm">
                      <button
                        onClick={(e) => this.handleUpdateExerciseRM(form, e)}
                        data-button-type="primary"
                        type="button"
                      >
                        {this.props.t("common:save")}
                      </button>
                      <button
                        onClick={(e) => this.handleUpdateExerciseRMDismiss(e)}
                        data-button-type="secondary"
                        type="button"
                      >
                        <SvgIcon
                          src="/img/new-icons/red-cross.svg"
                          noFilterOnLight={true}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="group alt-add-sets">
                <AddExerciseSets
                  selectedExercise={selectedExercise}
                  primary={primary}
                  sets={sets}
                  locals={locals}
                  handleReorderSet={this.handleReorderSet}
                  handleSetEditModeToggle={this.handleSetEditModeToggle}
                  handleRemoveSet={this.handleRemoveSet}
                  editSetBackup={editSetBackup}
                  intensityMeasure={intensityMeasure}
                  isRiR={isRiR}
                  exerciseType={exerciseType}
                  secondaryRepMaximum={secondaryRepMaximum}
                  activeUser={activeUser}
                  isEditing={isEditing}
                  activeUserToRequest={activeUserToRequest}
                  defaultRepMax={defaultRepMax}
                  highlight={this.state.highlightSet}
                />

                {isEditing ? (
                  <p className="add-set-hint">
                    {this.props.t("addExerciseModal.hintEditingSet", {
                      number: editSet.idx + 1,
                    })}
                  </p>
                ) : (
                  <SetRowHint
                    primary={primary}
                    selectedExercise={selectedExercise}
                    newSet={newSet}
                    intensityMeasure={intensityMeasure}
                    isRiR={isRiR}
                    rowValid={isNewSetValid}
                    rowEmpty={rowEmpty}
                    exerciseHasSets={exerciseHasSets}
                    queueEnabled={this.queueEnabled}
                    exerciseName={exerciseName}
                  />
                )}

                {isEditing ? (
                  <EditSetRow
                    editSet={cloneDeep(editSet)}
                    defaultRepMax={defaultRepMax}
                    form={cloneDeep(form)}
                    formSets={cloneDeep(sets)}
                    setIndex={editSet.idx}
                    handleEditSetChange={this.handleEditSetChange}
                    handleUpdateSet={this.handleUpdateSet}
                    handleUnitChange={this.handleUnitChange}
                    isAssistant={isAssistant}
                    activeUser={this.activeUser}
                    preferredLoadInput={preferredLoadInput}
                    setPreferredLoadInput={(value) => {
                      this.setState((prevState) => {
                        const { editSet } = prevState;
                        if (value === "drop") {
                          editSet.load = 0;
                          editSet.percentRepMax = 0;
                        } else {
                          editSet.loadDrop = 0;
                        }
                        return {
                          preferredLoadInput: value,
                          editSet,
                        };
                      });
                    }}
                    primary={primary}
                    selectedExercise={selectedExercise}
                    onCancelEdit={() =>
                      this.handleSetEditModeToggle(editSet.idx, editSetBackup)
                    }
                  />
                ) : (
                  <AddSetRow
                    primary={primary}
                    selectedExercise={selectedExercise}
                    handleNewSetChange={this.handleNewSetChange}
                    isRiR={isRiR}
                    intensityMeasure={intensityMeasure}
                    isAssistant={isAssistant}
                    handleUnitChange={this.handleUnitChange}
                    newSet={newSet}
                    preferredLoadInput={preferredLoadInput}
                    setPreferredLoadInput={(value) => {
                      this.setState((prevState) => {
                        const { newSet } = prevState;
                        if (value === "drop") {
                          newSet.load = 0;
                          newSet.percentRepMax = 0;
                        } else {
                          newSet.loadDrop = 0;
                        }
                        return {
                          preferredLoadInput: value,
                          newSet,
                        };
                      });
                    }}
                    isNewSetValid={isNewSetValid}
                    handleAddSet={this.handleAddSet}
                    handleParameterInputChange={this.handleParameterInputChange}
                    activeUser={activeUser}
                    parametersForm={this.state.parametersForm}
                    buttonFlow={{
                      rowEmpty,
                      exerciseHasSets,
                      queueEnabled: this.queueEnabled,
                      exerciseName,
                      onAddToWorkout: this.handleAddNextExercise,
                    }}
                  />
                )}
              </div>

              <div className="form-row">
                <div className="form-row-item flex flex-wrap items-center gap-4">
                  <label className="flex! items-center gap-1 cursor-pointer opacity-100!">
                    <input
                      type="checkbox"
                      checked={form.repsPerSide}
                      onChange={this.toggleRepsPerSide}
                    />
                    <SvgIcon src="/img/new-icons/reps-per-side.svg" />
                    {this.props.t("addExerciseModal.repsPerSide")}
                  </label>
                  <label className="flex! items-center gap-1 cursor-pointer opacity-100!">
                    <input
                      type="checkbox"
                      checked={tempoChecked}
                      onChange={this.toggleTempo}
                    />
                    <SvgIcon src="/img/new-icons/tempo.svg" />
                    {this.props.t("addExerciseModal.tempo")}
                  </label>
                  {tempoChecked && (
                    <div className="flex flex-col gap-1">
                      <input
                        ref={this.tempoInputRef}
                        type="text"
                        value={form.tempo}
                        onChange={this.onTempoInput}
                        placeholder={this.props.t(
                          "addExerciseModal.tempoPlaceholder"
                        )}
                        className="form-input"
                      />
                      {tempoError && (
                        <span className="text-(--danger) text-sm">
                          {this.props.t("addExerciseModal.tempoRequiredError")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-row-item">
                  <label htmlFor="notes">{this.props.t("common:notes")}</label>
                  <textarea
                    tabIndex="0"
                    name="notes"
                    defaultValue={editExerciseMode ? editExercise.notes : ""}
                    onChange={this.handleChange}
                    placeholder={this.props.t(
                      "addExerciseModal.notesPlaceholder"
                    )}
                  />
                </div>
              </div>

              {libraryMetadataNotice.show ? (
                <ExerciseLibraryMetadataGapsNotice
                  key={libraryMetadataSelectionKey(form)}
                  ref={this.libraryMetadataPanelRef}
                  missingKeys={libraryMetadataNotice.missingKeys}
                  record={libraryMetadataNotice.record}
                  libraryIndex={libraryMetadataNotice.libraryIndex}
                  formPrimary={form.primary}
                  isPrimaryExercise={libraryMetadataNotice.isPrimaryExercise}
                  repMaximum={libraryMetadataNotice.repMaximum}
                  athleteId={this.context.locals.activeUserToRequest._id}
                />
              ) : null}
            </div>

            {this.state.error ? (
              <p role="alert" className="px-6 pb-2 text-right text-(--danger)">
                {this.state.error}
              </p>
            ) : null}

            {this.editWorkout ? (
              this.renderEditFooter(editChanges, buttonStates)
            ) : (
              <footer>
                {userOrganizationCanAccessToExerciseLibrary ? (
                  <button
                    tabIndex="0"
                    type="button"
                    data-button-type="secondary"
                    onClick={() =>
                      this.setState({
                        addingMovementToLibrary: true,
                        newMovementRM: lround(defaultRepMax, 2),
                        form: Object.assign(form, {
                          primary: "squat",
                        }),
                      })
                    }
                  >
                    {this.props.t("addExerciseModal.createNewExercise")}
                  </button>
                ) : (
                  <div className="invisible" />
                )}

                <div className="flex gap-2">
                  <button
                    data-button-type="secondary"
                    onClick={(e) =>
                      this.queueEnabled
                        ? this.handleQueueCancel(e)
                        : this.handleCancel(e)
                    }
                    className="text-(--danger)!"
                    type="button"
                  >
                    {this.queueEnabled && this.state.editingQueueIndex !== null
                      ? this.props.t("workoutQueue.cancelEdit")
                      : this.props.t("common:cancel")}
                  </button>
                  <button
                    id={ADD_EXERCISE_PRIMARY_ACTION_ID}
                    className="add-to-workout-button"
                    data-button-type={
                      buttonStates[
                        this.queueEnabled ? "addToWorkout" : "save"
                      ] === "primary"
                        ? "primary"
                        : "secondary"
                    }
                    type="button"
                    onClick={(e) => {
                      if (this.state.editingSet) {
                        return this.triggerWarning(e);
                      }
                      return this.queueEnabled
                        ? this.handleAddNextExercise(e)
                        : this.handleSubmit(e);
                    }}
                    disabled={
                      buttonStates[
                        this.queueEnabled ? "addToWorkout" : "save"
                      ] === "disabled"
                    }
                  >
                    {this.queueEnabled && (
                      <SvgIcon src="/img/new-icons/add.svg" useCurrentColor />
                    )}
                    {!this.queueEnabled
                      ? this.props.t("common:save")
                      : this.state.editingQueueIndex !== null
                        ? this.props.t("workoutQueue.updateExercise")
                        : this.props.t("workoutQueue.addNextExercise")}
                  </button>
                </div>
              </footer>
            )}
          </form>
        )}
      </Modal>
    );
  }

  /** Edit Workout: "Exercise n of N", Undo / Remove / Previous / Next, banners. */
  renderEditNav(openItem) {
    const { t } = this.props;
    const editable = this.state.queueItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => isEditableItem(item));
    const position = editable.findIndex(
      ({ index }) => index === this.state.editingQueueIndex
    );
    const state = openItem ? itemState(openItem) : "new";
    const hasPrevious = position === -1 ? editable.length > 0 : position > 0;
    const hasNext = position !== -1 && position < editable.length - 1;

    return (
      <>
        <div className="edit-workout-nav">
          <span className="edit-workout-position">
            {position === -1
              ? t("editWorkout.newExercise")
              : t("editWorkout.position", {
                  index: position + 1,
                  count: editable.length,
                })}
          </span>
          <div className="flex gap-2 flex-wrap justify-end">
            {state === "edited" && !this.editFormChanged() && (
              <button
                type="button"
                data-button-type="text"
                onClick={() => this.undoEditItem()}
              >
                {t("editWorkout.undoChanges")}
              </button>
            )}
            {openItem?.serverId && !openItem.locked && (
              <button
                type="button"
                data-button-type="text"
                className={openItem.removed ? "" : "text-(--danger)!"}
                onClick={() =>
                  this.handleRemoveQueueItem(this.state.editingQueueIndex)
                }
              >
                {openItem.removed
                  ? t("editWorkout.restore")
                  : t("editWorkout.remove")}
              </button>
            )}
            <button
              type="button"
              data-button-type="secondary"
              disabled={!hasPrevious}
              onClick={() => this.stepEditItem(-1)}
            >
              {t("editWorkout.previous")}
            </button>
            <button
              type="button"
              data-button-type="secondary"
              disabled={!hasNext}
              onClick={() => this.stepEditItem(1)}
            >
              {t("editWorkout.next")}
            </button>
          </div>
        </div>
        {openItem?.removed && (
          <p className="edit-workout-banner danger">
            {t("editWorkout.removedBanner")}
          </p>
        )}
        {openItem?.locked && (
          <p className="edit-workout-banner">{t("editWorkout.loggedBanner")}</p>
        )}
      </>
    );
  }

  /** Edit Workout footer: unsaved summary or the discard bar, Cancel, Add exercise. */
  renderEditFooter(changes, buttonStates) {
    const { t } = this.props;
    const composingNew = this.state.editingQueueIndex === null;
    const summary = [
      changes.edited &&
        t("editWorkout.summaryEdited", { count: changes.edited }),
      changes.added && t("editWorkout.summaryAdded", { count: changes.added }),
      changes.removed &&
        t("editWorkout.summaryRemoved", { count: changes.removed }),
    ].filter(Boolean);

    return (
      <footer>
        {this.state.discardBarOpen ? (
          <div className="edit-workout-discard" role="alert">
            <span>
              {t("editWorkout.discardQuestion", {
                count: Math.max(changes.count, 1),
              })}
            </span>
            <button
              type="button"
              data-button-type="secondary"
              onClick={() => this.setState({ discardBarOpen: false })}
            >
              {t("editWorkout.keepEditing")}
            </button>
            <button
              type="button"
              data-button-type="secondary"
              className="text-(--danger)!"
              onClick={() => this.discardEditChanges()}
            >
              {t("editWorkout.discardChanges")}
            </button>
          </div>
        ) : (
          <span className="edit-workout-summary">
            {summary.length
              ? t("editWorkout.unsaved", { summary: summary.join(" · ") })
              : changes.any
                ? t("editWorkout.unsavedLayout")
                : t("editWorkout.noChanges")}
          </span>
        )}

        <div className="flex gap-2">
          <button
            data-button-type="secondary"
            onClick={() => this.handleEditClose()}
            className="text-(--danger)!"
            type="button"
          >
            {t("common:cancel")}
          </button>
          <button
            id={ADD_EXERCISE_PRIMARY_ACTION_ID}
            className="add-to-workout-button"
            data-button-type="secondary"
            type="button"
            disabled={composingNew && buttonStates.addToWorkout === "disabled"}
            onClick={async (e) => {
              if (this.state.editingSet) return this.triggerWarning(e);
              if (composingNew && !(await this.commitEditForm())) return;
              return this.startNewEditExercise();
            }}
          >
            <SvgIcon src="/img/new-icons/add.svg" useCurrentColor />
            {composingNew
              ? t("workoutQueue.addNextExercise")
              : t("editWorkout.addExercise")}
          </button>
        </div>
      </footer>
    );
  }
}

AddExerciseModal.contextType = RillContext;

export default withTranslation(["coaching-portal", "common"])(AddExerciseModal);
